import { state, getServerName } from './state';
import { API_BASE, get, post } from './api';
import type { Server, ServerMember, Person, Job, Shift, Connection, Agreement, Approval, LogEntry, Role } from './types';
import type { Channel } from './types';
import { renderServerRail } from './layout/server-rail';
import type { ServerRailCallbacks } from './layout/server-rail';
import { renderChannelList, loadChannelsForServer } from './layout/channel-list';
import type { ChannelListCallbacks } from './layout/channel-list';
import { renderContentArea, renderContentSkeleton } from './layout/content-area';
import { openDmConversation } from './pages/dms';
import type { HomeCallbacks } from './pages/home';
import { renderBookingPublic, isPublicBookingPath } from './pages/booking-public';
import { renderSupportLink, isPublicSupportLinkPath } from './pages/support-link';
import { renderAgreementSign, isPublicSigningPath } from './pages/agreement-sign';
import { renderSupportProfileSetup, isSupportProfileSetupPath } from './pages/support-profile-setup';
import { renderSupportOnboarding, renderSupportAgreementDraftOnly, isSupportOnboardingPath, isSupportAgreementDraftPath } from './pages/support-onboarding';
import { renderNdisExtraction, isNdisExtractionPath } from './pages/ndis-extraction';
import { renderNdisInvoiceDraft, isNdisInvoiceDraftPath } from './pages/ndis-invoice-draft';
import { isProviderInvoiceReviewPath, renderProviderInvoiceReview } from './pages/provider-invoice-review';
import { isParticipantProfileBuilderPath, renderParticipantProfileBuilder } from './pages/participant-profile-builder';
import { isProviderProfileBuilderPath, renderProviderProfileBuilder } from './pages/provider-profile-builder';
import { renderSupportOnboardingDemo, isSupportOnboardingDemoPath } from './pages/support-onboarding-demo';
import { renderNdisApprove, isNdisApprovePath } from './pages/ndis-approve';
import { renderNdisConsole, isNdisConsolePath } from './pages/ndis-console';
import { renderSupportWorkerOnboarding, isSupportWorkerOnboardingPath } from './pages/support-worker-onboarding';
import { renderReviewToken, isReviewTokenPath } from './pages/review-token';
import { renderGiteaBrowser, isGiteaBrowserPath } from './pages/gitea-browser';
import { renderNamedGuestPage, isNamedGuestPath } from './pages/named-guest';
import { renderGuestTestPage, isGuestTestPath } from './pages/guest-test';
import { renderGuestControlsTestPage, isGuestControlsTestPath } from './pages/guest-controls-test';
import { renderAdminMigrationDashboard, isAdminMigrationPath } from './pages/admin-migration-dashboard';
import { renderGuestExperience, isGuestExperiencePath } from './pages/guest-experience';
import { renderLanding } from './pages/landing';
import { showModal, closeModal, modalFrame, confirmModal } from './components/modal';
import { openUserSettings } from './components/user-settings';
import { openQuickSwitcher } from './components/quick-switcher';
import { openKeyboardShortcuts } from './components/keyboard-shortcuts';
import { showToast, errToast } from './components/toast';
import { validateRequired, clearAllErrors } from './components/form-validation';
import { mountCallOverlay } from './components/call-overlay';
import { initInstallBanner } from './components/install-banner';
import { installGlobalNotifier, refreshGlobalSubscriptions } from './components/global-notifier';
import { realtime } from './realtime';
import type { RealtimeEvent } from './realtime';
import { notifyForMessage } from './components/notifications';
import { iconUsers, iconTrash, iconHash, iconPlus, iconEdit, iconCopy, iconSettings } from './icons';
import { renderRoles } from './channels/roles';
import { setChannelUnread } from './layout/channel-list';

interface UnreadInfo { server_id: string; channel: string; unread_count: number; mention_count: number; }

let dataLoaded = false;

// Set by checkAuth() when a brand-new person row is created against a fresh
// hub-core identity. The init() flow checks this AFTER auth + BEFORE loadAll
// to gate the display-name onboarding modal (batch 7z item B).
let justSignedUp = false;
let signupDefaultName = '';

async function loadUnreads(): Promise<void> {
  if (!state.ownerId) return;
  try {
    const unreads = await get<UnreadInfo[]>(`/api/unreads?person_id=${encodeURIComponent(state.ownerId)}`);
    // Clear all unread state first — channels are data-driven, so iterate the
    // cached server_channels rather than a hardcoded list.
    state.channels
      .filter(c => c.server_id === state.currentServer)
      .forEach(c => setChannelUnread(c.name, false));
    // Aggregate mention count per server for the rail's numeric badge
    // (Discord parity). Cleared each pass so resolved mentions disappear.
    const { setServerMentions, setServerUnread } = await import('./layout/server-rail');
    const seenServers = new Set<string>();
    let totalUnread = 0;
    const mentionByServer: Record<string, number> = {};
    // Set unreads for current server
    for (const u of unreads) {
      totalUnread += u.unread_count;
      if (u.server_id === state.currentServer && u.unread_count > 0) {
        setChannelUnread(u.channel, true);
      }
      if (u.unread_count > 0) {
        seenServers.add(u.server_id);
        if (u.server_id !== state.currentServer) setServerUnread(u.server_id, true);
      }
      mentionByServer[u.server_id] = (mentionByServer[u.server_id] ?? 0) + (u.mention_count || 0);
    }
    // Clear unread state for servers no longer in the list
    for (const s of state.servers) {
      if (!seenServers.has(s.id)) setServerUnread(s.id, false);
      setServerMentions(s.id, mentionByServer[s.id] ?? 0);
    }
    // Update tab title with unread count (Discord-style)
    document.title = totalUnread > 0 ? `(${totalUnread}) Hub People` : 'Hub People';

    // Surface the in-app "Enable notifications?" banner when the user has
    // unread activity but hasn't yet granted permission. Never auto-calls
    // Notification.requestPermission() — that must be user-gesture-triggered.
    if (totalUnread > 0) {
      const mod = await import('./components/notifications');
      mod.showPermissionBannerIfNeeded();
    }
  } catch { /* ignore */ }
}

/** Build /api/servers URL with person_id filter so bot-owned servers stay hidden. */
function serversUrl(): string {
  return state.ownerId ? `/api/servers?person_id=${encodeURIComponent(state.ownerId)}` : '/api/servers';
}

async function loadAll(): Promise<void> {
  const [servers, serverMembers, people, jobs, shifts, connections, agreements, approvals, logs, roles] = await Promise.all([
    get<Server[]>(serversUrl()).catch(() => [] as Server[]),
    get<ServerMember[]>('/api/server_members').catch(() => [] as ServerMember[]),
    get<Person[]>('/api/people').catch(() => [] as Person[]),
    get<Job[]>('/api/jobs').catch(() => [] as Job[]),
    get<Shift[]>('/api/shifts').catch(() => [] as Shift[]),
    get<Connection[]>('/api/connections').catch(() => [] as Connection[]),
    get<Agreement[]>('/api/agreements').catch(() => [] as Agreement[]),
    get<Approval[]>('/api/approvals').catch(() => [] as Approval[]),
    get<LogEntry[]>('/api/logs').catch(() => [] as LogEntry[]),
    get<Role[]>('/api/roles').catch(() => [] as Role[]),
  ]);
  state.servers = servers;
  state.serverMembers = serverMembers;
  state.people = people;
  state.jobs = jobs;
  state.shifts = shifts;
  state.connections = connections;
  state.agreements = agreements;
  state.approvals = approvals;
  state.logs = logs;
  state.roles = roles;
  dataLoaded = true;

  // Load channels for the currently selected server (if any)
  if (state.currentServer) {
    await loadChannelsForServer(state.currentServer);
    const serverChans = state.channels.filter(c => c.server_id === state.currentServer && c.channel_type !== 'voice');
    const currentStr = typeof state.currentChannel === 'string' ? state.currentChannel : '';
    const isVoiceSentinel = currentStr.startsWith('voice:');
    if (!isVoiceSentinel && !serverChans.some(c => c.name === state.currentChannel)) {
      const first = serverChans[0];
      if (first) state.currentChannel = first.name;
    }
  }

  // Load unread counts
  void loadUnreads();

  // Mount the global notifier so SMS/MMS/incoming calls fire a popup +
  // sound regardless of which channel page is open.
  installGlobalNotifier();

  // Reconnect ntfy push SSE if the user previously opted in (dev8 P2 P4).
  // Explicit user action required to first subscribe — this only reconnects.
  const { reconnectNtfyIfSubscribed } = await import('./components/ntfy-subscribe');
  reconnectNtfyIfSubscribed();

  // Server lifecycle listener (Mitchell ask 2026-04-26): live-refresh the
  // rail when another client creates, renames, or deletes a server. Uses
  // the existing reloadServersAndRender() so the cheap diff suppresses
  // unnecessary re-renders.
  realtime.on((ev: RealtimeEvent) => {
    if (ev.type === 'server_created' || ev.type === 'server_updated' || ev.type === 'server_deleted') {
      void reloadServersAndRender();
    }
    if (ev.type === 'channel_created' || ev.type === 'channel_updated' || ev.type === 'channel_deleted') {
      // Re-fetch AND re-render — loading alone updates state but leaves the
      // sidebar stale (new/renamed/deleted channels didn't show until a switch).
      if (state.currentServer) void loadChannelsForServer(state.currentServer).then(() => renderApp());
    }
    if (ev.type === 'message_created') {
      // Fire a web notification for someone else's message in a channel that's
      // NOT on screen (never for my own message or the channel I'm viewing —
      // that would be spam). Only such a "notifiable" message also triggers the
      // one-time enable-push nudge, so my own typing never pops the banner.
      const notifiable = notifyForMessage(ev.message);
      if (notifiable) void import('./components/ntfy-subscribe').then((m) => m.maybeShowPushBanner());
    }
  });
}

// ── Callbacks ──

const homeCallbacks: HomeCallbacks = {
  onSwitchServer: switchServer,
  onCreateServer: showCreateServerModal,
  onJoinServer: showJoinServerModal,
};

const railCallbacks: ServerRailCallbacks = {
  onSwitchServer: switchServer,
  onCreateServer: showCreateServerModal,
  onJoinServer: showJoinServerModal,
  onManageMembers: showManageMembersModal,
  onServerSettings: showServerSettingsModal,
  onAppSettings: showAppSettingsModal,
  onLeaveServer: confirmLeaveServer,
};

const channelListCallbacks: ChannelListCallbacks = {
  onSwitchChannel: switchChannel,
  onServerSettings: showServerSettingsModal,
  onManageMembers: showManageMembersModal,
  onManageRoles: switchChannel,
  onLeaveServer: confirmLeaveServer,
  onChannelsChanged: () => renderApp(),
};

/** Render a Discord-style home overview in the channel-list slot when no server
 *  is selected. Discord DM sidebar: Friends pill + DIRECT MESSAGES list only.
 *  Servers live exclusively in the 72px rail — never repeated here. */
function renderHomeChannelOverview(container: HTMLElement): void {
  // User bar (Discord parity — always show avatar + controls at bottom of
  // channel list, even on the home / DM view). D5 desktop layout fix.
  const me = state.people.find(p => p.id === state.ownerId);
  const userName = me?.name || 'You';
  const userInitial = (userName.charAt(0) || '?').toUpperCase();
  // presence_status would be the live online/away/dnd state (migration 051);
  // not yet on the typed Person model — fallback to status.
  const userStatus = (me as { presence_status?: string } | undefined)?.presence_status || me?.status || 'active';

  container.innerHTML = `<div class="cl-header">Direct Messages</div>
    <div class="cl-channels cl-home-overview">
      <div class="cl-channel cl-home-friends" data-home="friends">
        <span class="cl-channel-icon">${__iconUsersInline()}</span>
        <span class="cl-channel-name">Friends</span>
      </div>
    </div>
    <div class="cl-user-bar cl-user-panel" role="region" aria-label="User profile bar">
      <div class="cl-user-trigger" id="cl-home-account-trigger" title="Account, status, profile">
        <div class="cl-user-avatar-wrap">
          <div class="cl-user-avatar">${escHtml(userInitial)}</div>
          <div class="cl-user-status-dot cl-status-${escAttr(userStatus)}"></div>
        </div>
        <div class="cl-user-info">
          <div class="cl-user-name">${escHtml(userName)}</div>
          <div class="cl-user-status-text">${escHtml(userStatus === 'active' ? 'Online' : userStatus)}</div>
        </div>
      </div>
      <div class="cl-user-controls">
        <div class="cl-ctrl-split" data-ctrl="mic">
          <button class="cl-ctrl-btn cl-ctrl-btn-main" id="cl-home-mic-btn" title="Mute" aria-label="Mute microphone">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
          </button>
        </div>
        <div class="cl-ctrl-split" data-ctrl="deafen">
          <button class="cl-ctrl-btn cl-ctrl-btn-main" id="cl-home-deafen-btn" title="Deafen" aria-label="Deafen audio">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
          </button>
        </div>
        <button class="cl-ctrl-btn" id="cl-home-settings-btn" title="User Settings" aria-label="User Settings">${iconSettings()}</button>
      </div>
    </div>`;

  // User-bar settings button (D5 desktop fix)
  container.querySelector('#cl-home-settings-btn')?.addEventListener('click', () => openUserSettings());
  container.querySelector('#cl-home-account-trigger')?.addEventListener('click', () => openUserSettings());

  // Mic/deafen toggles — persist to localStorage, same as channel-list.ts
  const homeMicBtn = container.querySelector('#cl-home-mic-btn');
  const homeDeafenBtn = container.querySelector('#cl-home-deafen-btn');
  if (localStorage.getItem('hub-people-muted') === 'true') homeMicBtn?.classList.add('cl-ctrl-active');
  if (localStorage.getItem('hub-people-deafened') === 'true') homeDeafenBtn?.classList.add('cl-ctrl-active');
  homeMicBtn?.addEventListener('click', (e) => {
    const btn = e.currentTarget as HTMLElement;
    btn.classList.toggle('cl-ctrl-active');
    const muted = btn.classList.contains('cl-ctrl-active');
    localStorage.setItem('hub-people-muted', String(muted));
    btn.title = muted ? 'Unmute' : 'Mute';
  });
  homeDeafenBtn?.addEventListener('click', (e) => {
    const btn = e.currentTarget as HTMLElement;
    btn.classList.toggle('cl-ctrl-active');
    const deafened = btn.classList.contains('cl-ctrl-active');
    localStorage.setItem('hub-people-deafened', String(deafened));
    btn.title = deafened ? 'Undeafen' : 'Deafen';
  });

  // Friends row → render the Discord-style Friends view (Online / All /
  // Pending / Blocked / Add Friend) into the content area. The friend graph
  // doesn't have a real backend yet, but the spec requires the surface so
  // we render against state.people for All/Online and stub the rest.
  container.querySelector<HTMLElement>('.cl-home-friends')?.addEventListener('click', () => {
    const content = document.getElementById('content-area');
    if (!content) return;
    container.querySelectorAll<HTMLElement>('.cl-channel.active').forEach(el => el.classList.remove('active'));
    container.querySelector<HTMLElement>('.cl-home-friends')?.classList.add('active');
    renderFriendsView(content);
  });
}

type FriendsTab = 'online' | 'all' | 'pending' | 'blocked' | 'add';

function renderFriendsView(content: HTMLElement, tab: FriendsTab = 'online'): void {
  const myId = state.ownerId || state.people[0]?.id || '';
  const others = state.people.filter(p => p.id !== myId);
  const online = others.filter(p => p.status === 'active');
  const tabs: Array<{ id: FriendsTab; label: string; count?: number }> = [
    { id: 'online',  label: 'Online',  count: online.length },
    { id: 'all',     label: 'All',     count: others.length },
    { id: 'pending', label: 'Pending', count: 0 },
    { id: 'blocked', label: 'Blocked', count: 0 },
    { id: 'add',     label: 'Add Friend' },
  ];

  const tabBar = tabs.map(t => {
    const active = t.id === tab ? ' fr-tab-active' : '';
    const badge = (t.id !== 'add' && (t.count ?? 0) > 0) ? `<span class="fr-tab-count">${t.count}</span>` : '';
    return `<button class="fr-tab${active}" data-friends-tab="${t.id}">${escHtml(t.label)}${badge}</button>`;
  }).join('');

  let body = '';
  if (tab === 'online' || tab === 'all') {
    const list = (tab === 'online' ? online : others);
    if (list.length === 0) {
      body = '<div class="fr-empty">No one to show.</div>';
    } else {
      body = `<div class="fr-list-header">${tab === 'online' ? 'Online' : 'All Friends'} — ${list.length}</div>` +
        list.map(p => {
          const initial = (p.name || '?').charAt(0).toUpperCase();
          const isOnline = p.status === 'active';
          return `<div class="fr-row" data-person="${escAttr(p.id)}">
            <div class="fr-row-avatar">
              <div class="fr-avatar">${escHtml(initial)}</div>
              <div class="fr-status-dot fr-status-${isOnline ? 'online' : 'offline'}"></div>
            </div>
            <div class="fr-row-info">
              <div class="fr-row-name">${escHtml(p.name)}</div>
              <div class="fr-row-status">${isOnline ? 'Online' : 'Offline'}</div>
            </div>
            <div class="fr-row-actions">
              <button class="fr-row-btn" data-fr-action="dm" title="Send Message" aria-label="Send Message">${__iconChatInline()}</button>
            </div>
          </div>`;
        }).join('');
    }
  } else if (tab === 'pending') {
    body = '<div class="fr-empty">No pending friend requests.</div>';
  } else if (tab === 'blocked') {
    body = '<div class="fr-empty">No blocked users.</div>';
  } else if (tab === 'add') {
    body = `<div class="fr-add">
      <h3>Add Friend</h3>
      <p>You can add friends with their Hub username or display name.</p>
      <div class="fr-add-row">
        <input type="text" class="fr-add-input" id="fr-add-input" placeholder="Username" />
        <button class="fr-add-submit" id="fr-add-submit">Send Friend Request</button>
      </div>
    </div>`;
  }

  content.innerHTML = `
    <div class="fr-header">
      <span class="fr-header-title">Friends</span>
      <div class="fr-tabs">${tabBar}</div>
    </div>
    <div class="fr-body">${body}</div>
  `;

  content.querySelectorAll<HTMLButtonElement>('[data-friends-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const next = btn.dataset['friendsTab'] as FriendsTab | undefined;
      if (next) renderFriendsView(content, next);
    });
  });

  content.querySelectorAll<HTMLElement>('.fr-row').forEach(row => {
    row.querySelector<HTMLButtonElement>('[data-fr-action="dm"]')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const pid = row.dataset['person'];
      if (pid) openDmConversation(pid, state.ownerId || state.people[0]?.id || '');
    });
  });

  const addBtn = document.getElementById('fr-add-submit');
  addBtn?.addEventListener('click', () => {
    const input = document.getElementById('fr-add-input') as HTMLInputElement | null;
    const v = input?.value.trim();
    if (!v) { showToast('Enter a username first', 'err'); return; }
    showToast('Friend requests are not yet wired up. (Use the directory channel to add people.)', 'ok');
    if (input) input.value = '';
  });
}

function __iconChatInline(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
}

function __iconUsersInline(): string {
  return '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>';
}

// Re-fetch servers + memberships and re-render the rail. Fired by Discover
// after a Join, by Server Settings after a delete, and by any future flow
// that mutates server membership without doing a full data reload.
async function reloadServersAndRender(): Promise<void> {
  try {
    const [servers, serverMembers] = await Promise.all([
      get<Server[]>(serversUrl()).catch(() => [] as Server[]),
      get<ServerMember[]>('/api/server_members').catch(() => [] as ServerMember[]),
    ]);
    // Cheap diff so we don't thrash the rail on quiet polls.
    const sameLen = servers.length === state.servers.length
      && serverMembers.length === state.serverMembers.length;
    const sameIds = sameLen
      && servers.every((s, i) => state.servers[i] && s.id === state.servers[i]!.id)
      && serverMembers.every((m, i) => {
          const cur = state.serverMembers[i];
          return !!cur && m.server_id === cur.server_id && m.person_id === cur.person_id && m.role === cur.role;
      });
    if (sameIds) return;
    state.servers = servers;
    state.serverMembers = serverMembers;
    renderApp();
    refreshGlobalSubscriptions();
  } catch { /* swallow */ }
}
window.addEventListener('hub-servers-changed', () => { void reloadServersAndRender(); });

// Mitchell rule (2026-04-26): "stale state reaching me is a build/deploy
// bug, fix it server-side." Out-of-band changes (admin migrations, server
// joins/leaves done by other clients, eviction sweeps) don't fire the
// hub-servers-changed event. Two passive triggers re-fetch on their own:
//   - Tab regains focus / becomes visible → instant refetch
//   - Background poll every 60s while the tab is open
// No user action required. Cheap diff above suppresses re-render churn.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    void reloadServersAndRender();
    void checkBundleVersion();
  }
});
window.setInterval(() => {
  if (document.visibilityState === 'visible') {
    void reloadServersAndRender();
    void checkBundleVersion();
  }
}, 60_000);

// Bundle-version watchdog (Mitchell ask 2026-04-26: "make sure the devs
// dont like make me hard refresh"). The backend's GET /api/version
// returns the bundle hash currently served from static/index.html. We
// capture the hash at boot and compare on every poll/visibility tick.
// On mismatch we soft-reload the SPA — `location.reload()` keeps the
// session cookie + restores the user's place via the URL — so a
// just-shipped UI fix reaches the user within ~60s with zero F5.
let bootBundleHash: string | null = null;
let reloadInFlight = false;
async function captureBootBundleHash(): Promise<void> {
  try {
    const r = await fetch(`${API_BASE}/api/version`, { credentials: 'include' });
    if (!r.ok) return;
    const j = await r.json() as { data?: { bundle?: string } };
    bootBundleHash = j.data?.bundle ?? null;
  } catch { /* swallow — re-tries on next poll */ }
}
async function checkBundleVersion(): Promise<void> {
  if (bootBundleHash == null || reloadInFlight) return;
  try {
    const r = await fetch(`${API_BASE}/api/version`, { credentials: 'include' });
    if (!r.ok) return;
    const j = await r.json() as { data?: { bundle?: string } };
    const cur = j.data?.bundle ?? '';
    if (cur && cur !== bootBundleHash) {
      reloadInFlight = true; // stop re-polling; the banner stays until the user acts
      showUpdateBanner();
    }
  } catch { /* swallow */ }
}

/** Non-destructive update prompt: a dismissible TOP HEADER banner with a Reload
 *  button, instead of a forced reload — so a deploy never kills an active call or
 *  a half-typed message. The user reloads when ready; place is restored on boot
 *  (see the hp-restore-server/channel logic in init()).
 *
 *  It mounts as the FIRST child of <body> (a flex column with #app at flex:1),
 *  so the bar takes a full-width row at the very top and pushes the whole app
 *  shell down rather than overlaying it — a proper header, not a floating toast. */
function showUpdateBanner(): void {
  if (document.getElementById('hp-update-banner')) return;
  const bar = document.createElement('div');
  bar.id = 'hp-update-banner';
  bar.setAttribute('role', 'status');
  bar.style.cssText =
    'flex-shrink:0;z-index:2147483000;width:100%;box-sizing:border-box;' +
    'display:flex;align-items:center;justify-content:center;gap:14px;' +
    'padding:9px 16px;' +
    'background:var(--accent);color:var(--fg-on-accent);' +
    'font:600 14px/1.25 system-ui,-apple-system,Segoe UI,sans-serif;' +
    'box-shadow:0 2px 10px rgba(0,0,0,.22)';
  const label = document.createElement('span');
  label.textContent = 'A new version of Hub People is ready.';
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Reload';
  reload.style.cssText =
    'flex-shrink:0;border:0;cursor:pointer;padding:7px 18px;border-radius:8px;' +
    'background:#fff;color:#111;font:700 13px/1 system-ui,sans-serif';
  reload.addEventListener('click', () => {
    try {
      if (state.currentServer) sessionStorage.setItem('hp-restore-server', state.currentServer);
      if (state.currentChannel) sessionStorage.setItem('hp-restore-channel', String(state.currentChannel));
    } catch { /* quota / private mode */ }
    window.location.reload();
  });
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '✕';
  dismiss.style.cssText =
    'flex-shrink:0;border:0;cursor:pointer;background:transparent;color:inherit;' +
    'font:700 16px/1 system-ui;opacity:.85;padding:4px 4px';
  dismiss.addEventListener('click', () => bar.remove());
  bar.append(label, reload, dismiss);
  document.body.insertBefore(bar, document.body.firstChild);
}
void captureBootBundleHash();

function renderApp(): void {
  const rail = document.getElementById('server-rail');
  const channels = document.getElementById('channel-list');
  const content = document.getElementById('content-area');
  if (!rail || !channels || !content) return;

  renderServerRail(rail, railCallbacks);

  // Channel list visibility (batch 7.5 audit):
  // Desktop (>768px): always visible. When on home with no server selected,
  // render a helpful overview. This matches Discord's always-on left column.
  // Mobile (≤768px): CSS handles the drawer via transform + .open class.
  channels.style.display = '';
  if (state.currentServer) {
    renderChannelList(channels, channelListCallbacks);
  } else {
    renderHomeChannelOverview(channels);
  }

  if (dataLoaded) {
    renderContentArea(content, homeCallbacks);
  } else {
    renderContentSkeleton(content);
  }
}

function switchServer(id: string | null): void {
  state.currentServer = id;
  state.currentChannel = 'general';
  const srv = id ? state.servers.find(s => s.id === id) : null;
  if (srv?.is_mirror === 1) {
    document.body.setAttribute('data-current-server-mirror', '1');
  } else {
    document.body.removeAttribute('data-current-server-mirror');
  }
  if (id) {
    // Update server rail highlight immediately for responsive feel, then
    // render once after channels load — avoids the double-render flash.
    document.querySelectorAll<HTMLElement>('.sr-icon[data-server]').forEach(el => {
      el.classList.toggle('active', el.dataset['server'] === id);
    });
    void loadChannelsForServer(id).then(() => {
      const serverChans = state.channels.filter(c => c.server_id === id && c.channel_type !== 'voice');
      const hasCurrent = serverChans.some(c => c.name === state.currentChannel);
      if (!hasCurrent) {
        const first = serverChans[0];
        if (first) state.currentChannel = first.name;
      }
      renderApp();
    });
  } else {
    renderApp();
  }
}

function switchChannel(ch: Channel): void {
  state.currentChannel = ch;
  renderApp();
}

// ── Create Server Modal ──

function showCreateServerModal(): void {
  showModal(modalFrame('Create Team',
    `<div class="form-group">
      <label class="form-label" for="f-server-name">Team Name</label>
      <input class="form-input" id="f-server-name" placeholder="My Team" />
    </div>
    <div class="form-group">
      <label class="form-label" for="f-server-type">Type</label>
      <select class="form-input" id="f-server-type">
        <option value="personal">Personal</option>
        <option value="community">Community</option>
        <option value="business">Business</option>
      </select>
    </div>
    <div class="form-group">
      <label class="form-label" for="f-server-icon">Icon Letter</label>
      <input class="form-input" id="f-server-icon" maxlength="2" placeholder="M" />
    </div>
    <div class="form-group">
      <label class="form-label" for="f-server-desc">Description</label>
      <textarea class="form-input" id="f-server-desc" rows="3" placeholder="What is this team about?"></textarea>
    </div>
    <div class="form-group">
      <label class="form-label" for="f-server-vis">Visibility</label>
      <select class="form-input" id="f-server-vis">
        <option value="private">Private</option>
        <option value="internal">Internal</option>
        <option value="public">Public</option>
      </select>
    </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Cancel</button>
     <button class="btn btn-primary" id="modal-save">Create</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-save')?.addEventListener('click', async () => {
    clearAllErrors();
    if (!validateRequired('f-server-name', 'Name')) return;

    const name = (document.getElementById('f-server-name') as HTMLInputElement).value.trim();
    const serverType = (document.getElementById('f-server-type') as HTMLSelectElement).value;
    const icon = (document.getElementById('f-server-icon') as HTMLInputElement).value.trim();
    const description = (document.getElementById('f-server-desc') as HTMLTextAreaElement).value.trim();
    const visibility = (document.getElementById('f-server-vis') as HTMLSelectElement).value;

    let ownerId = state.people[0]?.id ?? '';
    if (!ownerId) {
      const person = await post<Person>('/api/people/add', { name: 'You', role: 'Owner' });
      ownerId = person.id;
    }

    await post<Server>('/api/servers/add', { name, type: serverType, icon, description, visibility, owner_id: ownerId });
    closeModal();
    await loadAll();
    state.currentServer = state.servers[state.servers.length - 1]?.id ?? null;
    renderApp();
    showToast('Team created');
  });
}

// ── Invite Preview Modal (batch 7z) ──
//
// When a brand-new (or returning) member lands on the app with `?invite=CODE`
// or `#invite/CODE` in the URL, we DON'T immediately POST the join. Instead we
// fetch the public preview (UNAUTH-safe `/api/invites/:code/info`) and show
// a Discord-style modal with server icon, name, member count, inviter, and
// Accept / Decline buttons. The join only fires on Accept.

interface InvitePreviewInfo {
  valid: boolean;
  reason?: string;
  server_id?: string;
  server_name?: string;
  server_icon?: string;
  member_count?: number;
  expires_at?: string | null;
  max_uses?: number | null;
  uses?: number;
  inviter_name?: string;
}

function cleanInviteUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  let hash = url.hash || '';
  if (hash.startsWith('#invite/')) hash = '';
  history.replaceState({}, '', url.pathname + url.search + hash);
}

function renderInitialAvatar(name: string): string {
  const initial = (name.trim().charAt(0) || '?').toUpperCase();
  return `<div class="ip-avatar-fallback">${escHtml(initial)}</div>`;
}

function renderServerIcon(icon: string, name: string): string {
  if (icon && /^(https?:|data:|\/)/.test(icon)) {
    return `<img class="ip-avatar-img" src="${escAttr(icon)}" alt="" />`;
  }
  return renderInitialAvatar(name);
}

async function showInvitePreview(code: string): Promise<void> {
  let info: InvitePreviewInfo;
  try {
    info = await get<InvitePreviewInfo>('/api/invites/' + encodeURIComponent(code) + '/info');
  } catch {
    cleanInviteUrl();
    showToast('This invite is no longer valid', 'err');
    return;
  }

  if (!info.valid) {
    cleanInviteUrl();
    const reason = info.reason === 'expired' ? 'expired'
      : info.reason === 'max_uses_reached' ? 'reached its limit'
      : 'no longer valid';
    showToast(`This invite is ${reason}`, 'err');
    return;
  }

  const myId = state.ownerId;
  const isMember = myId
    ? state.servers.some(s => s.id === info.server_id)
    : false;

  const serverName = info.server_name || 'this server';
  const memberCount = info.member_count ?? 0;
  const inviter = info.inviter_name || 'a member';

  if (isMember) {
    showModal(modalFrame('Already a member',
      `<div class="invite-preview">
        <div class="ip-server-icon">${renderServerIcon(info.server_icon || '', serverName)}</div>
        <div class="ip-server-name">${escHtml(serverName)}</div>
        <p class="ip-sub">You're already a member of this server.</p>
      </div>`,
      `<button class="btn btn-primary" id="ip-open">Open Server</button>`
    ));
    document.getElementById('modal-close')?.addEventListener('click', () => { closeModal(); cleanInviteUrl(); });
    document.getElementById('ip-open')?.addEventListener('click', () => {
      closeModal();
      cleanInviteUrl();
      if (info.server_id) switchServer(info.server_id);
    });
    return;
  }

  showModal(modalFrame(`You've been invited`,
    `<div class="invite-preview">
      <div class="ip-server-icon">${renderServerIcon(info.server_icon || '', serverName)}</div>
      <div class="ip-server-name">${escHtml(serverName)}</div>
      <div class="ip-meta">
        <span class="ip-online-dot"></span>
        ${escHtml(String(memberCount))} ${memberCount === 1 ? 'member' : 'members'}
      </div>
      <p class="ip-inviter">Invited by <strong>${escHtml(inviter)}</strong></p>
    </div>`,
    `<button class="btn btn-secondary" id="ip-decline">Decline</button>
     <button class="btn btn-primary ip-accept" id="ip-accept">Accept Invite</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', () => {
    closeModal();
    cleanInviteUrl();
  });
  document.getElementById('ip-decline')?.addEventListener('click', () => {
    closeModal();
    cleanInviteUrl();
  });
  document.getElementById('ip-accept')?.addEventListener('click', async () => {
    if (!myId) {
      showToast('You must be signed in to accept an invite', 'err');
      return;
    }
    const btn = document.getElementById('ip-accept') as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = 'Joining…'; }
    try {
      await post('/api/invites/join/' + encodeURIComponent(code), { person_id: myId });
      closeModal();
      cleanInviteUrl();
      showToast(`Joined ${serverName}`, 'ok');
      await loadAll();
      if (info.server_id) switchServer(info.server_id);
      else renderApp();
    } catch (e) {
      showToast('Could not join: ' + String(e), 'err');
      if (btn) { btn.disabled = false; btn.textContent = 'Accept Invite'; }
    }
  });
}

// ── Display-name Onboarding Modal (batch 7z item B) ──
//
// Runs after checkAuth() creates a fresh person row from a new hub-core
// identity, BEFORE the first loadAll/renderApp. Lets the new member pick a
// display name + an avatar accent color so they don't land in the app as a
// generic "User". Skipped on subsequent loads via a per-owner localStorage
// flag (set on Continue OR on close — we don't nag).

const ONBOARD_PALETTE: ReadonlyArray<string> = [
  '#16a34a', // brand green
  '#3b82f6', // blue
  '#a855f7', // purple
  '#ec4899', // pink
  '#f59e0b', // amber
  '#ef4444', // red
];

function onboardingFlagKey(ownerId: string): string {
  return 'hub-people-onboarded:' + ownerId;
}

function avatarColorKey(ownerId: string): string {
  return 'hub-people-avatar-color:' + ownerId;
}

function showDisplayNameOnboarding(defaultName: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const ownerId = state.ownerId || '';
    const safeDefault = (defaultName || '').slice(0, 32);
    const initial = (safeDefault.trim().charAt(0) || '?').toUpperCase();
    const initialColor = ONBOARD_PALETTE[0] || '#16a34a';

    const swatches = ONBOARD_PALETTE.map((c, i) => `
      <button type="button"
              class="ob-swatch${i === 0 ? ' selected' : ''}"
              data-color="${escAttr(c)}"
              style="background:${escAttr(c)}"
              aria-label="Avatar color ${escAttr(c)}"></button>`).join('');

    showModal(modalFrame('Welcome to the hub ecosystems',
      `<div class="onboarding">
        <p class="ob-sub">Pick a display name so people can find you.</p>
        <div class="ob-avatar-row">
          <div class="ob-avatar" id="ob-avatar" style="background:${escAttr(initialColor)}">
            <span id="ob-avatar-initial">${escHtml(initial)}</span>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label" for="ob-name">Display name</label>
          <input class="form-input" id="ob-name" type="text"
                 value="${escAttr(safeDefault)}" maxlength="32"
                 placeholder="Your name" autocomplete="nickname" />
        </div>
        <div class="form-group">
          <label class="form-label">Avatar color</label>
          <div class="ob-swatches" role="radiogroup" aria-label="Avatar color">${swatches}</div>
        </div>
      </div>`,
      `<button class="btn btn-primary ip-accept" id="ob-continue" type="button">Continue</button>`
    ));

    const nameInput = document.getElementById('ob-name') as HTMLInputElement | null;
    const avatarEl = document.getElementById('ob-avatar') as HTMLElement | null;
    const initialEl = document.getElementById('ob-avatar-initial') as HTMLElement | null;
    const continueBtn = document.getElementById('ob-continue') as HTMLButtonElement | null;

    let chosenColor = initialColor;

    const refreshInitial = (): void => {
      if (!initialEl || !nameInput) return;
      const v = nameInput.value.trim();
      initialEl.textContent = (v.charAt(0) || '?').toUpperCase();
    };

    nameInput?.addEventListener('input', refreshInitial);

    document.querySelectorAll<HTMLButtonElement>('.ob-swatch').forEach(btn => {
      btn.addEventListener('click', () => {
        chosenColor = btn.dataset['color'] || initialColor;
        document.querySelectorAll<HTMLElement>('.ob-swatch').forEach(el => el.classList.remove('selected'));
        btn.classList.add('selected');
        if (avatarEl) avatarEl.style.background = chosenColor;
      });
    });

    const finish = (): void => {
      if (ownerId) {
        try { localStorage.setItem(onboardingFlagKey(ownerId), 'true'); } catch { /* quota */ }
      }
      closeModal();
      resolve();
    };

    document.getElementById('modal-close')?.addEventListener('click', finish);

    continueBtn?.addEventListener('click', async () => {
      const name = (nameInput?.value || '').trim().slice(0, 32);
      if (!name) {
        showToast('Please pick a display name', 'err');
        nameInput?.focus();
        return;
      }
      if (continueBtn) { continueBtn.disabled = true; continueBtn.textContent = 'Saving…'; }
      try {
        if (ownerId) {
          await post('/api/people/update', { id: ownerId, name });
          try { localStorage.setItem(avatarColorKey(ownerId), chosenColor); } catch { /* quota */ }
        }
      } catch (e) {
        showToast('Could not save: ' + String(e), 'err');
        if (continueBtn) { continueBtn.disabled = false; continueBtn.textContent = 'Continue'; }
        return;
      }
      finish();
    });

    // Enter to submit
    nameInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); continueBtn?.click(); }
    });
  });
}

// ── Join Server Modal ──

function showJoinServerModal(): void {
  showModal(modalFrame('Join Team',
    `<div class="form-group">
      <label class="form-label" for="f-invite-code">Enter invite code or server URL</label>
      <input class="form-input" id="f-invite-code" placeholder="e.g. abc123 or https://..." />
    </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Cancel</button>
     <button class="btn btn-primary" id="modal-save">Join Server</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('modal-save')?.addEventListener('click', async () => {
    clearAllErrors();
    if (!validateRequired('f-invite-code', 'Invite code')) return;

    const code = (document.getElementById('f-invite-code') as HTMLInputElement).value.trim();
    try {
      await post('/api/servers/join', { code });
      closeModal();
      await loadAll();
      renderApp();
      showToast('Joined server');
    } catch (err) {
      showToast('Could not join server', 'err');
    }
  });
}

// ── Server Settings Modal ──

// Discord-1:1 Server Settings. Left sidebar (SERVER / USER MANAGEMENT /
// MODERATION / CHANNELS + Delete Server at bottom) + right content pane.
// Overview preserves the prior flat form (Name / Type / Description /
// Visibility / Invite). Other sections embed existing surfaces inline.

type SssSection =
  | 'overview' | 'roles' | 'emoji' | 'vanity' | 'audit'
  | 'members' | 'invites' | 'bans' | 'safety'
  | 'channels' | 'delete';

interface SssInvite {
  id: string; server_id: string; code: string;
  created_by: string; max_uses: number | null; uses: number;
  expires_at: string | null; created_at: string;
}

function sssSidebarHtml(active: SssSection): string {
  const item = (k: SssSection, label: string, icon = ''): string =>
    `<button class="us-nav-item ${k === active ? 'us-nav-active' : ''}"
       data-sss="${k}" type="button" role="tab" aria-selected="${k === active}">
       ${icon ? `<span class="us-nav-icon">${icon}</span>` : ''}
       <span class="us-nav-label">${label}</span>
     </button>`;
  return `
    <div class="us-nav-title">Server</div>
    ${item('overview', 'Overview')}
    ${item('roles', 'Roles')}
    ${item('emoji', 'Emoji')}
    ${item('vanity', 'Vanity URL')}
    ${item('audit', 'Audit Log')}
    <div class="us-nav-title">User Management</div>
    ${item('members', 'Members')}
    ${item('invites', 'Invites')}
    ${item('bans', 'Bans')}
    <div class="us-nav-title">Moderation</div>
    ${item('safety', 'Safety Setup')}
    <div class="us-nav-title">Channels</div>
    ${item('channels', 'Channels & Threads')}
    <div class="us-nav-separator"></div>
    <button class="us-nav-item us-nav-danger ${active === 'delete' ? 'us-nav-active' : ''}"
      data-sss="delete" type="button" role="tab" aria-selected="${active === 'delete'}">
      <span class="us-nav-icon">${iconTrash()}</span>
      <span class="us-nav-label">Delete Server</span>
    </button>
  `;
}

function showServerSettingsModal(serverId: string): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;

  // Ensure embedded renderers that read state.currentServer (e.g. renderRoles)
  // see the server whose settings we're editing. Restore on close.
  const prevServer = state.currentServer;
  state.currentServer = serverId;

  let active: SssSection = 'overview';

  showModal(modalFrame('Server Settings',
    `<div class="sss-shell">
       <aside class="sss-sidebar" id="sss-sidebar" role="tablist" aria-label="Server settings sections">
         ${sssSidebarHtml(active)}
       </aside>
       <section class="sss-content" id="sss-content" role="tabpanel" aria-live="polite"></section>
     </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Close</button>`
  ));

  const modalEl = document.getElementById('modal');
  modalEl?.classList.add('modal-sss');

  const doClose = (): void => {
    modalEl?.classList.remove('modal-sss');
    state.currentServer = prevServer;
    closeModal();
  };

  document.getElementById('modal-close')?.addEventListener('click', doClose);
  document.getElementById('modal-cancel')?.addEventListener('click', doClose);

  const rerenderSidebar = (): void => {
    const sb = document.getElementById('sss-sidebar');
    if (sb) sb.innerHTML = sssSidebarHtml(active);
    wireSidebar();
  };

  const switchSection = (next: SssSection): void => {
    active = next;
    document.querySelectorAll<HTMLButtonElement>('#sss-sidebar [data-sss]').forEach(btn => {
      const on = btn.dataset['sss'] === next;
      btn.classList.toggle('us-nav-active', on);
      btn.setAttribute('aria-selected', String(on));
    });
    const pane = document.getElementById('sss-content');
    if (!pane) return;
    renderServerSettingsSection(next, serverId, pane, switchSection);
  };

  const wireSidebar = (): void => {
    document.querySelectorAll<HTMLButtonElement>('#sss-sidebar [data-sss]').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset['sss'] as SssSection | undefined;
        if (key) switchSection(key);
      });
    });
  };

  wireSidebar();
  void rerenderSidebar; // reserved for future dynamic sidebar updates

  // Keyboard navigation on sidebar: ↑/↓ move focus, Enter activates.
  document.getElementById('sss-sidebar')?.addEventListener('keydown', (e: Event) => {
    const ke = e as KeyboardEvent;
    if (ke.key !== 'ArrowUp' && ke.key !== 'ArrowDown' && ke.key !== 'Enter') return;
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('#sss-sidebar [data-sss]'));
    if (buttons.length === 0) return;
    const curr = document.activeElement as HTMLElement | null;
    const idx = buttons.findIndex(b => b === curr);
    if (ke.key === 'Enter') {
      (curr as HTMLButtonElement | null)?.click();
      ke.preventDefault();
      return;
    }
    const base = idx < 0 ? 0 : idx;
    const nextIdx = ke.key === 'ArrowDown'
      ? (base + 1) % buttons.length
      : (base - 1 + buttons.length) % buttons.length;
    buttons[nextIdx]?.focus();
    ke.preventDefault();
  });

  switchSection('overview');
}

function renderServerSettingsSection(
  section: SssSection,
  serverId: string,
  pane: HTMLElement,
  switchSection: (s: SssSection) => void,
): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) { pane.innerHTML = '<p style="color:var(--fg-muted)">Server not found.</p>'; return; }

  switch (section) {
    case 'overview':   renderSssOverview(pane, serverId); break;
    case 'roles':      renderSssRoles(pane); break;
    case 'emoji':      renderSssComingSoon(pane, 'Emoji', 'Custom server emoji — coming soon.'); break;
    case 'vanity':     renderSssVanity(pane, serverId); break;
    case 'audit':      renderSssAudit(pane, serverId); break;
    case 'members':    renderSssMembers(pane, serverId); break;
    case 'invites':    renderSssInvites(pane, serverId); break;
    case 'bans':       renderSssComingSoon(pane, 'Bans', 'Ban list — coming soon.'); break;
    case 'safety':     renderSssComingSoon(pane, 'Safety Setup', 'Moderation rules — coming soon.'); break;
    case 'channels':   renderSssChannels(pane, serverId); break;
    case 'delete':     renderSssDelete(pane, serverId, switchSection); break;
  }
}

function sssHeader(title: string, sub?: string): string {
  return `<header class="sss-pane-header">
    <h2 class="sss-pane-title">${escHtml(title)}</h2>
    ${sub ? `<p class="sss-pane-sub">${escHtml(sub)}</p>` : ''}
  </header>`;
}

function renderSssOverview(pane: HTMLElement, serverId: string): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;

  pane.innerHTML = `
    ${sssHeader('Server Overview')}
    <div class="sss-form">
      <div class="form-group">
        <label class="form-label" for="f-ss-name">Server Name</label>
        <input class="form-input" id="f-ss-name" value="${escAttr(server.name)}" maxlength="64" />
      </div>
      <div class="form-group">
        <label class="form-label">Server Type</label>
        <div class="sss-radio-row" role="radiogroup">
          ${(['personal', 'community', 'business'] as const).map(t => `
            <label class="sss-radio">
              <input type="radio" name="f-ss-type" value="${t}" ${server.type === t ? 'checked' : ''}>
              <span class="sss-radio-dot" aria-hidden="true"></span>
              <span class="sss-radio-label">${t[0]!.toUpperCase() + t.slice(1)}</span>
            </label>`).join('')}
        </div>
      </div>
      <div class="form-group">
        <label class="form-label" for="f-ss-desc">Description</label>
        <textarea class="form-input" id="f-ss-desc" rows="3" maxlength="280">${escHtml(server.description)}</textarea>
      </div>
      <div class="form-group">
        <label class="form-label">Visibility</label>
        <div class="sss-radio-row" role="radiogroup">
          ${(['private', 'internal', 'public'] as const).map(v => `
            <label class="sss-radio">
              <input type="radio" name="f-ss-vis" value="${v}" ${server.visibility === v ? 'checked' : ''}>
              <span class="sss-radio-dot" aria-hidden="true"></span>
              <span class="sss-radio-label">${v[0]!.toUpperCase() + v.slice(1)}</span>
            </label>`).join('')}
        </div>
      </div>
      <div class="form-group sss-inline-invite">
        <label class="form-label">Invite Link</label>
        <div style="display:flex;gap:8px">
          <input class="form-input" id="f-ss-invite" readonly placeholder="Click Generate…" style="flex:1;font-family:monospace;font-size:12px" />
          <button class="btn btn-primary" id="f-ss-gen-invite" type="button" style="white-space:nowrap">Generate</button>
        </div>
        <p class="sss-help">Share this link to let people join your server.</p>
      </div>
    </div>

    ${state.ownerId === server.owner_id ? `
      <div class="sss-danger-zone">
        <h3 class="sss-danger-heading">Danger Zone</h3>
        <p class="sss-danger-sub">Owner-only actions. Both are irreversible.</p>
        <div class="sss-danger-row">
          <div class="sss-danger-text">
            <strong>Transfer Ownership</strong>
            <p>Hand this server over to another member. You become an admin and lose owner privileges.</p>
          </div>
          <button class="btn btn-secondary" id="f-ss-transfer" type="button">Transfer Ownership</button>
        </div>
        <div class="sss-danger-row">
          <div class="sss-danger-text">
            <strong>Delete Server</strong>
            <p>Permanently delete this server, every channel, every message, every invite. There is no undo.</p>
          </div>
          <button class="btn btn-danger" id="f-ss-delete" type="button">Delete Server</button>
        </div>
      </div>
    ` : ''}
    <div class="sss-save-bar" id="f-ss-save-bar" hidden>
      <span class="sss-save-msg">Careful — you have unsaved changes!</span>
      <div class="sss-save-actions">
        <button class="btn btn-secondary" id="f-ss-reset" type="button">Reset</button>
        <button class="btn btn-primary" id="f-ss-save" type="button">Save Changes</button>
      </div>
    </div>
  `;

  const nameEl = document.getElementById('f-ss-name') as HTMLInputElement | null;
  const descEl = document.getElementById('f-ss-desc') as HTMLTextAreaElement | null;
  const bar = document.getElementById('f-ss-save-bar');
  const resetBtn = document.getElementById('f-ss-reset');
  const saveBtn = document.getElementById('f-ss-save');

  type ServerType = Server['type'];
  type ServerVisibility = Server['visibility'];
  const initial: { name: string; type: ServerType; description: string; visibility: ServerVisibility } = {
    name: server.name,
    type: server.type,
    description: server.description,
    visibility: server.visibility,
  };

  const readRadio = <T extends string>(selector: string, fallback: T): T => {
    const v = pane.querySelector<HTMLInputElement>(selector)?.value;
    return (v as T | undefined) ?? fallback;
  };

  const current = (): typeof initial => ({
    name: (nameEl?.value || '').trim(),
    type: readRadio<ServerType>('input[name="f-ss-type"]:checked', initial.type),
    description: (descEl?.value || '').trim(),
    visibility: readRadio<ServerVisibility>('input[name="f-ss-vis"]:checked', initial.visibility),
  });

  const refreshDirty = (): void => {
    const c = current();
    const dirty = c.name !== initial.name
      || c.type !== initial.type
      || c.description !== initial.description
      || c.visibility !== initial.visibility;
    if (bar) bar.hidden = !dirty;
  };

  pane.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
    .forEach(el => el.addEventListener('input', refreshDirty));
  pane.querySelectorAll<HTMLInputElement>('input[type="radio"]')
    .forEach(el => el.addEventListener('change', refreshDirty));

  resetBtn?.addEventListener('click', () => {
    if (nameEl) nameEl.value = initial.name;
    if (descEl) descEl.value = initial.description;
    pane.querySelectorAll<HTMLInputElement>('input[name="f-ss-type"]').forEach(r => { r.checked = r.value === initial.type; });
    pane.querySelectorAll<HTMLInputElement>('input[name="f-ss-vis"]').forEach(r => { r.checked = r.value === initial.visibility; });
    refreshDirty();
  });

  saveBtn?.addEventListener('click', async () => {
    clearAllErrors();
    if (!validateRequired('f-ss-name', 'Name')) return;
    const c = current();
    try {
      await post('/api/servers/update', { id: serverId, ...c });
      await loadAll();
      renderApp();
      showToast('Server updated', 'ok');
      // Update initial snapshot to reflect saved state, then hide the bar.
      Object.assign(initial, c);
      refreshDirty();
    } catch (err) {
      errToast(err);
    }
  });

  document.getElementById('f-ss-gen-invite')?.addEventListener('click', async () => {
    const myId = state.ownerId || state.people[0]?.id || '';
    try {
      const result = await post<{ code: string }>('/api/invites/create', {
        server_id: serverId,
        created_by: myId,
        expires_hours: 168,
      });
      const link = `${window.location.origin}/people/#invite/${result.code}`;
      const input = document.getElementById('f-ss-invite') as HTMLInputElement | null;
      if (input) {
        input.value = link;
        input.select();
        void navigator.clipboard.writeText(link).then(() => showToast('Invite link copied', 'ok'));
      }
    } catch (e) { showToast(String(e), 'err'); }
  });

  // Transfer ownership — owner-only, opens a member-picker modal that
  // lists every other member of this server. Pick → "type the server name
  // to confirm" gate → POST /api/servers/:id/transfer. After success the
  // settings modal closes and the rail re-renders so the role badge flips.
  document.getElementById('f-ss-transfer')?.addEventListener('click', () => {
    showTransferOwnershipModal(serverId);
  });

  // Delete server — owner-only, "type the server name" confirm gate then
  // POST /api/servers/:id/delete. cascade_delete_server in the backend
  // wipes channels/messages/roles/invites; on the frontend we just close
  // the settings modal and refresh state.
  document.getElementById('f-ss-delete')?.addEventListener('click', () => {
    showDeleteServerModal(serverId);
  });
}

// ── Transfer Ownership modal ─────────────────────────────────────────
function showTransferOwnershipModal(serverId: string): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;
  const myId = state.ownerId || '';
  // Members other than the current owner are eligible recipients.
  const candidates = state.serverMembers
    .filter(m => m.server_id === serverId && m.person_id !== server.owner_id)
    .map(m => state.people.find(p => p.id === m.person_id))
    .filter((p): p is Person => !!p);

  const optionsHtml = candidates.length === 0
    ? '<p class="sss-help">No other members in this server. Invite someone first, then transfer to them.</p>'
    : `<select class="form-input" id="xfer-recipient">
         ${candidates.map(p => `<option value="${escAttr(p.id)}">${escHtml(p.name)}</option>`).join('')}
       </select>`;

  showModal(modalFrame('Transfer Ownership',
    `<p style="margin-top:0">You are about to hand <strong>${escHtml(server.name)}</strong> to another member. You will become an admin and lose owner privileges. This cannot be undone.</p>
     <div class="form-group">
       <label class="form-label" for="xfer-recipient">New Owner</label>
       ${optionsHtml}
     </div>
     <div class="form-group">
       <label class="form-label" for="xfer-confirm">Type <strong>${escHtml(server.name)}</strong> to confirm</label>
       <input class="form-input" id="xfer-confirm" autocomplete="off" />
     </div>`,
    `<button class="btn btn-secondary" id="xfer-cancel" type="button">Cancel</button>
     <button class="btn btn-danger" id="xfer-go" type="button" ${candidates.length === 0 ? 'disabled' : ''}>Transfer Ownership</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('xfer-cancel')?.addEventListener('click', closeModal);
  document.getElementById('xfer-go')?.addEventListener('click', async () => {
    const newOwner = (document.getElementById('xfer-recipient') as HTMLSelectElement | null)?.value;
    const typed = (document.getElementById('xfer-confirm') as HTMLInputElement | null)?.value || '';
    if (typed.trim() !== server.name) { showToast('Server name does not match', 'err'); return; }
    if (!newOwner) { showToast('Pick a recipient', 'err'); return; }
    try {
      await post(`/api/servers/${encodeURIComponent(serverId)}/transfer`, {
        current_owner_id: myId,
        new_owner_id: newOwner,
      });
      closeModal();
      await loadAll();
      renderApp();
      showToast('Ownership transferred', 'ok');
    } catch (e) { showToast(String(e), 'err'); }
  });
}

// ── Delete Server modal ──────────────────────────────────────────────
function showDeleteServerModal(serverId: string): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;
  showModal(modalFrame('Delete Server',
    `<p style="margin-top:0">You are about to permanently delete <strong>${escHtml(server.name)}</strong>. Every channel, every message, every invite, every role will be wiped. There is no undo.</p>
     <div class="form-group">
       <label class="form-label" for="del-confirm">Type <strong>${escHtml(server.name)}</strong> to confirm</label>
       <input class="form-input" id="del-confirm" autocomplete="off" />
     </div>`,
    `<button class="btn btn-secondary" id="del-cancel" type="button">Cancel</button>
     <button class="btn btn-danger" id="del-go" type="button">Delete Server</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('del-cancel')?.addEventListener('click', closeModal);
  document.getElementById('del-go')?.addEventListener('click', async () => {
    const typed = (document.getElementById('del-confirm') as HTMLInputElement | null)?.value || '';
    if (typed.trim() !== server.name) { showToast('Server name does not match', 'err'); return; }
    try {
      await post(`/api/servers/${encodeURIComponent(serverId)}/delete`, {});
      closeModal();
      // Server is gone; clear the active server selection so the rail
      // re-renders without a dangling pointer.
      if (state.currentServer === serverId) state.currentServer = null;
      await loadAll();
      renderApp();
      showToast(`"${server.name}" deleted`, 'ok');
    } catch (e) { showToast(String(e), 'err'); }
  });
}

function renderSssComingSoon(pane: HTMLElement, title: string, body: string): void {
  pane.innerHTML = `
    ${sssHeader(title)}
    <div class="sss-empty">
      <div class="sss-empty-badge">Coming Soon</div>
      <p class="sss-empty-msg">${escHtml(body)}</p>
    </div>
  `;
}

function renderSssRoles(pane: HTMLElement): void {
  // renderRoles reads state.currentServer (we aligned it on modal open) and
  // paints its standard roles surface into the given body element.
  pane.innerHTML = '';
  renderRoles(pane);
}

function renderSssVanity(pane: HTMLElement, serverId: string): void {
  pane.innerHTML = `
    ${sssHeader('Vanity URL', 'Claim a custom invite URL for this server.')}
    <div class="sss-form">
      <div class="form-group">
        <label class="form-label">Custom URL</label>
        <div style="display:flex;gap:8px;align-items:center">
          <span class="sss-help" style="font-family:monospace;white-space:nowrap">${escHtml(window.location.origin)}/people/#invite/</span>
          <input class="form-input" id="f-vanity-slug" placeholder="my-team" maxlength="32"
            pattern="[a-z0-9-]{3,32}" autocomplete="off" style="font-family:monospace;flex:1" />
        </div>
        <p class="sss-help">3–32 chars: lowercase letters, digits, hyphens.</p>
      </div>
      <div>
        <button class="btn btn-primary" id="f-vanity-save" type="button">Create Vanity Invite</button>
      </div>
    </div>
  `;
  document.getElementById('f-vanity-save')?.addEventListener('click', async () => {
    const slug = (document.getElementById('f-vanity-slug') as HTMLInputElement | null)?.value.trim().toLowerCase() || '';
    if (!/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(slug)) {
      showToast('Invalid vanity slug', 'err');
      return;
    }
    const myId = state.ownerId || state.people[0]?.id || '';
    try {
      const result = await post<{ code: string }>('/api/invites/create', {
        server_id: serverId, created_by: myId, vanity: slug,
      });
      const link = `${window.location.origin}/people/#invite/${result.code}`;
      void navigator.clipboard.writeText(link);
      showToast('Vanity invite created', 'ok');
    } catch (e) { showToast(String(e), 'err'); }
  });
}

function renderSssAudit(pane: HTMLElement, serverId: string): void {
  pane.innerHTML = `
    ${sssHeader('Audit Log', 'Recent activity across this server.')}
    <div class="sss-audit" id="sss-audit-list"><p class="sss-empty-msg">Loading…</p></div>
  `;
  const container = document.getElementById('sss-audit-list');
  if (!container) return;

  // /api/logs returns the activity_log table (no server_id column). Filter
  // client-side to entries whose payload_summary mentions this server's id
  // OR whose handler is a server-scoped mutation (servers.*, roles.*,
  // invites.*, channels.*, server_members.*).
  const serverLike = /^(servers?\.|roles?\.|invites?\.|channels?\.|server_members\.)/;
  const entries = state.logs
    .filter(l => l.payload_summary.includes(serverId) || serverLike.test(l.handler))
    .slice(0, 200);

  if (entries.length === 0) {
    container.innerHTML = '<p class="sss-empty-msg">No audit entries yet.</p>';
    return;
  }

  const rows = entries.map(e => {
    const person = state.people.find(p => p.id === e.person_id);
    const actor = person?.name || (e.person_id ? escHtml(e.person_id.slice(0, 8)) : 'system');
    const initial = (person?.name || 'S').charAt(0).toUpperCase();
    const when = e.ts ? new Date(e.ts).toLocaleString() : '';
    return `<div class="sss-audit-row">
      <div class="avatar avatar-sm sss-audit-avatar">${escHtml(initial)}</div>
      <div class="sss-audit-body">
        <div class="sss-audit-line">
          <span class="sss-audit-actor">${escHtml(actor)}</span>
          <span class="sss-audit-action">${escHtml(e.handler)}</span>
          ${e.payload_summary ? `<span class="sss-audit-target">${escHtml(e.payload_summary.slice(0, 64))}</span>` : ''}
        </div>
        <div class="sss-audit-time">${escHtml(when)}</div>
      </div>
    </div>`;
  }).join('');

  container.innerHTML = rows;
}

function renderSssMembers(pane: HTMLElement, serverId: string): void {
  const members = state.serverMembers.filter(m => m.server_id === serverId);
  const rows = members.map(m => {
    const person = state.people.find(p => p.id === m.person_id);
    const name = person?.name ?? 'Unknown';
    const initial = name.charAt(0).toUpperCase();
    const joined = m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '';
    return `<div class="member-row" data-member-row="${escAttr(m.person_id)}">
      <div class="avatar avatar-sm" style="background: var(--accent)">${escHtml(initial)}</div>
      <div class="member-info">
        <div class="member-name">${escHtml(name)}</div>
        <div class="member-joined">Joined ${escHtml(joined)}</div>
      </div>
      <div class="member-actions">
        <select class="form-input" style="padding:2px 6px;font-size:11px;width:auto" data-member-id="${escAttr(m.person_id)}" data-member-role>
          <option value="owner" ${m.role === 'owner' ? 'selected' : ''}>Owner</option>
          <option value="admin" ${m.role === 'admin' ? 'selected' : ''}>Admin</option>
          <option value="member" ${m.role === 'member' ? 'selected' : ''}>Member</option>
          <option value="guest" ${m.role === 'guest' ? 'selected' : ''}>Guest</option>
        </select>
        <button class="btn btn-danger btn-sm" data-remove-member="${escAttr(m.person_id)}" type="button">Remove</button>
      </div>
    </div>`;
  }).join('') || '<div class="cl-empty-msg">No members yet</div>';

  pane.innerHTML = `
    ${sssHeader('Members', `${members.length} member${members.length === 1 ? '' : 's'}`)}
    <div class="sss-toolbar">
      <button class="btn btn-secondary" id="sss-add-member" type="button">${iconUsers()} Add Member</button>
    </div>
    <div class="member-list">${rows}</div>
  `;

  pane.querySelectorAll<HTMLSelectElement>('[data-member-role]').forEach(sel => {
    sel.addEventListener('change', async () => {
      const personId = sel.dataset['memberId'];
      if (!personId) return;
      try {
        await post('/api/server_members/update', { server_id: serverId, person_id: personId, role: sel.value });
        await loadAll();
        showToast('Role updated', 'ok');
      } catch { showToast('Failed to update role', 'err'); }
    });
  });

  pane.querySelectorAll<HTMLElement>('[data-remove-member]').forEach(btn => {
    btn.addEventListener('click', () => {
      const personId = btn.dataset['removeMember'];
      if (!personId) return;
      confirmModal('Remove member', 'Remove this member?', () => {
        void post('/api/server_members/delete', { server_id: serverId, person_id: personId })
          .then(async () => { await loadAll(); renderSssMembers(pane, serverId); showToast('Member removed', 'ok'); })
          .catch(() => showToast('Failed to remove member', 'err'));
      }, { confirmLabel: 'Remove', variant: 'danger' });
    });
  });

  document.getElementById('sss-add-member')?.addEventListener('click', () => {
    showAddMemberToSssModal(serverId, pane);
  });
}

function showAddMemberToSssModal(serverId: string, pane: HTMLElement): void {
  const existing = state.serverMembers.filter(m => m.server_id === serverId).map(m => m.person_id);
  const avail = state.people.filter(p => !existing.includes(p.id));
  const opts = ['<option value="">Select a person…</option>']
    .concat(avail.map(p => `<option value="${escAttr(p.id)}">${escHtml(p.name)}</option>`))
    .join('');

  // Open a sub-modal without destroying the server-settings modal. We swap the
  // modal innerHTML to the Add Member frame, then restore the settings modal
  // on confirm/cancel.
  const modalEl = document.getElementById('modal');
  const savedHtml = modalEl?.innerHTML ?? '';
  const savedWasWide = modalEl?.classList.contains('modal-sss') ?? false;
  modalEl?.classList.remove('modal-sss');

  showModal(modalFrame('Add Member',
    `<div class="form-group">
       <label class="form-label" for="f-add-person">Person</label>
       <select class="form-input" id="f-add-person">${opts}</select>
     </div>
     <div class="form-group">
       <label class="form-label" for="f-add-role">Role</label>
       <select class="form-input" id="f-add-role">
         <option value="member">Member</option>
         <option value="admin">Admin</option>
         <option value="guest">Guest</option>
       </select>
     </div>`,
    `<button class="btn btn-secondary" id="am-cancel" type="button">Cancel</button>
     <button class="btn btn-primary" id="am-save" type="button">Add</button>`
  ));

  const restore = (): void => {
    if (modalEl) modalEl.innerHTML = savedHtml;
    if (savedWasWide) modalEl?.classList.add('modal-sss');
    // Re-bind sidebar behaviour on the restored markup by redrawing the section.
    renderSssMembers(pane, serverId);
    // Re-attach header/footer close handlers on the restored server-settings shell.
    document.getElementById('modal-close')?.addEventListener('click', () => {
      modalEl?.classList.remove('modal-sss');
      closeModal();
    });
    document.getElementById('modal-cancel')?.addEventListener('click', () => {
      modalEl?.classList.remove('modal-sss');
      closeModal();
    });
    rewireSssSidebar(serverId);
  };

  document.getElementById('modal-close')?.addEventListener('click', restore);
  document.getElementById('am-cancel')?.addEventListener('click', restore);
  document.getElementById('am-save')?.addEventListener('click', async () => {
    const personId = (document.getElementById('f-add-person') as HTMLSelectElement).value;
    const role = (document.getElementById('f-add-role') as HTMLSelectElement).value;
    if (!personId) { showToast('Select a person', 'warn'); return; }
    try {
      await post('/api/server_members/add', { server_id: serverId, person_id: personId, role });
      await loadAll();
      restore();
      showToast('Member added', 'ok');
    } catch { showToast('Failed to add member', 'err'); }
  });
}

function rewireSssSidebar(serverId: string): void {
  // After a sub-modal swap, rebind sidebar click handlers so navigation
  // between sections still works. We render whatever tab button is marked
  // active. This is a lightweight rebinder for the restored DOM.
  document.querySelectorAll<HTMLButtonElement>('#sss-sidebar [data-sss]').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['sss'] as SssSection | undefined;
      if (!key) return;
      document.querySelectorAll<HTMLButtonElement>('#sss-sidebar [data-sss]').forEach(b => {
        const on = b.dataset['sss'] === key;
        b.classList.toggle('us-nav-active', on);
        b.setAttribute('aria-selected', String(on));
      });
      const content = document.getElementById('sss-content');
      if (content) renderServerSettingsSection(key, serverId, content, (k) => {
        // minimal closure — re-use rewire path
        document.querySelector<HTMLButtonElement>(`#sss-sidebar [data-sss="${k}"]`)?.click();
      });
    });
  });
}

function renderSssInvites(pane: HTMLElement, serverId: string): void {
  pane.innerHTML = `
    ${sssHeader('Invites', 'Active invite links for this server.')}
    <div class="sss-toolbar">
      <button class="btn btn-primary" id="sss-create-invite" type="button">${iconPlus()} Create Invite</button>
    </div>
    <div id="sss-invites-list"><p class="sss-empty-msg">Loading…</p></div>
  `;

  const loadList = async (): Promise<void> => {
    const container = document.getElementById('sss-invites-list');
    if (!container) return;
    try {
      const invites = await get<SssInvite[]>(`/api/invites?server_id=${encodeURIComponent(serverId)}`);
      if (invites.length === 0) {
        container.innerHTML = '<p class="sss-empty-msg">No active invites yet.</p>';
        return;
      }
      const rows = invites.map(inv => {
        const link = `${window.location.origin}/people/#invite/${inv.code}`;
        const expires = inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : 'Never';
        const usage = inv.max_uses ? `${inv.uses}/${inv.max_uses}` : `${inv.uses} uses`;
        return `<div class="sss-invite-row">
          <div class="sss-invite-main">
            <div class="sss-invite-code">${escHtml(inv.code)}</div>
            <div class="sss-invite-meta">Expires ${escHtml(expires)} · ${escHtml(usage)}</div>
          </div>
          <button class="btn btn-secondary btn-sm" data-inv-copy="${escAttr(link)}" type="button">${iconCopy()} Copy</button>
        </div>`;
      }).join('');
      container.innerHTML = rows;
      container.querySelectorAll<HTMLButtonElement>('[data-inv-copy]').forEach(btn => {
        btn.addEventListener('click', () => {
          const v = btn.dataset['invCopy'] || '';
          void navigator.clipboard.writeText(v);
          showToast('Invite copied', 'ok');
        });
      });
    } catch {
      container.innerHTML = '<p class="sss-empty-msg">Could not load invites.</p>';
    }
  };

  void loadList();

  document.getElementById('sss-create-invite')?.addEventListener('click', async () => {
    const myId = state.ownerId || state.people[0]?.id || '';
    try {
      await post<{ code: string }>('/api/invites/create', {
        server_id: serverId, created_by: myId, expires_hours: 168,
      });
      showToast('Invite created', 'ok');
      void loadList();
    } catch (e) { showToast(String(e), 'err'); }
  });
}

function renderSssChannels(pane: HTMLElement, serverId: string): void {
  const channels = state.channels.filter(c => c.server_id === serverId);
  const voiceChannels = state.voiceChannels.filter(v => v.server_id === serverId);
  const byCategory = new Map<string, typeof channels>();
  for (const c of channels) {
    const key = c.category || 'General';
    const list = byCategory.get(key) || [];
    list.push(c);
    byCategory.set(key, list);
  }

  if (channels.length === 0 && voiceChannels.length === 0) {
    pane.innerHTML = `
      ${sssHeader('Channels & Threads')}
      <div class="sss-empty"><p class="sss-empty-msg">No channels yet.</p></div>
    `;
    return;
  }

  const voiceIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>';

  let html = `${sssHeader('Channels & Threads', 'Edit or delete channels grouped by category.')}`;
  for (const [cat, list] of byCategory) {
    html += `<div class="sss-channel-group">
      <div class="sss-channel-category">${escHtml(cat)}
        <button class="btn-icon sss-cat-del" data-cat-del="${escAttr(cat)}" data-server-id="${escAttr(serverId)}" title="Delete category" type="button">${iconTrash()}</button>
      </div>`;
    for (const c of list.sort((a, b) => a.position - b.position)) {
      html += `<div class="sss-channel-row">
        <span class="sss-channel-icon">${iconHash()}</span>
        <span class="sss-channel-name">${escHtml(c.name)}</span>
        <button class="btn-icon sss-channel-edit" data-ch-edit="${escAttr(c.id)}" data-ch-name="${escAttr(c.name)}" title="Edit channel" type="button">${iconEdit()}</button>
        <button class="btn-icon sss-channel-del" data-ch-del="${escAttr(c.id)}" data-ch-name="${escAttr(c.name)}" title="Delete channel" type="button">${iconTrash()}</button>
      </div>`;
    }
    html += `</div>`;
  }

  if (voiceChannels.length > 0) {
    html += `<div class="sss-channel-group">
      <div class="sss-channel-category">Voice Channels</div>`;
    for (const vc of voiceChannels) {
      html += `<div class="sss-channel-row">
        <span class="sss-channel-icon">${voiceIcon}</span>
        <span class="sss-channel-name">${escHtml(vc.name)}</span>
        <button class="btn-icon sss-voice-del" data-vc-del="${escAttr(vc.id)}" data-vc-name="${escAttr(vc.name)}" title="Delete voice channel" type="button">${iconTrash()}</button>
      </div>`;
    }
    html += `</div>`;
  }

  pane.innerHTML = html;

  pane.querySelectorAll<HTMLButtonElement>('[data-ch-edit]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['chEdit'] || '';
      const name = btn.dataset['chName'] || '';
      void import('./components/channel-settings').then(({ openChannelSettings }) => {
        openChannelSettings(id, name, 'general');
      });
    });
  });
  pane.querySelectorAll<HTMLButtonElement>('[data-ch-del]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['chDel'] || '';
      const name = btn.dataset['chName'] || '';
      confirmModal('Delete channel', `Delete #${name}? This cannot be undone.`, () => {
        void post('/api/channels/delete', { id }).then(async () => {
          state.channels = state.channels.filter(c => c.id !== id);
          showToast(`#${name} deleted`, 'ok');
          renderSssChannels(pane, serverId);
        }).catch(e => showToast(String(e), 'err'));
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });
  pane.querySelectorAll<HTMLButtonElement>('[data-vc-del]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset['vcDel'] || '';
      const name = btn.dataset['vcName'] || '';
      confirmModal('Delete voice channel', `Delete #${name}? This cannot be undone.`, () => {
        void post('/api/voice-channels/delete', { id }).then(async () => {
          state.voiceChannels = state.voiceChannels.filter(v => v.id !== id);
          showToast(`#${name} deleted`, 'ok');
          renderSssChannels(pane, serverId);
        }).catch(e => showToast(String(e), 'err'));
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });
  pane.querySelectorAll<HTMLButtonElement>('[data-cat-del]').forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = btn.dataset['catDel'] || '';
      const sid = btn.dataset['serverId'] || '';
      const chansInCat = state.channels.filter(c => c.server_id === sid && (c.category || 'General') === cat);
      if (chansInCat.length > 0) {
        confirmModal('Delete category', `"${cat}" still contains ${chansInCat.length} channel(s). Move them to "General" and delete the category?`, () => {
          void Promise.all(chansInCat.map(c => post('/api/channels/update', { id: c.id, category: 'General' }))).then(() => {
            for (const c of chansInCat) c.category = 'General';
            showToast(`Category "${cat}" deleted`, 'ok');
            renderSssChannels(pane, sid);
          }).catch(e => showToast(String(e), 'err'));
        }, { confirmLabel: 'Move and delete', variant: 'danger' });
      } else {
        showToast(`Category "${cat}" removed`, 'ok');
        renderSssChannels(pane, sid);
      }
    });
  });
}

function renderSssDelete(pane: HTMLElement, serverId: string, _switchSection: (s: SssSection) => void): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;
  pane.innerHTML = `
    ${sssHeader('Delete Server', 'Deleting a server is permanent and cannot be undone.')}
    <div class="sss-danger-zone">
      <p class="sss-help">Type the server name <strong>${escHtml(server.name)}</strong> to confirm.</p>
      <input class="form-input" id="sss-delete-confirm" placeholder="${escAttr(server.name)}" autocomplete="off" />
      <button class="btn btn-danger" id="sss-delete-btn" type="button" disabled>${iconTrash()} Delete Server</button>
    </div>
  `;
  const input = document.getElementById('sss-delete-confirm') as HTMLInputElement | null;
  const btn = document.getElementById('sss-delete-btn') as HTMLButtonElement | null;
  input?.addEventListener('input', () => {
    if (btn) btn.disabled = (input.value || '').trim() !== server.name;
  });
  btn?.addEventListener('click', async () => {
    if ((input?.value || '').trim() !== server.name) return;
    try {
      await post('/api/servers/delete', { id: serverId });
      await loadAll();
      if (state.currentServer === serverId) state.currentServer = null;
      document.getElementById('modal')?.classList.remove('modal-sss');
      closeModal();
      renderApp();
      showToast('Server deleted', 'ok');
    } catch {
      showToast('Failed to delete server', 'err');
    }
  });
}

// ── Manage Members Modal ──

function showManageMembersModal(serverId: string): void {
  const server = state.servers.find(s => s.id === serverId);
  if (!server) return;

  const members = state.serverMembers.filter(m => m.server_id === serverId);

  let membersHtml = '<div class="member-list">';
  for (const m of members) {
    const person = state.people.find(p => p.id === m.person_id);
    const name = person?.name ?? 'Unknown';
    const initial = name.charAt(0).toUpperCase();
    const joined = m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '';

    membersHtml += `
      <div class="member-row">
        <div class="avatar avatar-sm" style="background: var(--accent)">${escHtml(initial)}</div>
        <div class="member-info">
          <div class="member-name">${escHtml(name)}</div>
          <div class="member-joined">Joined ${escHtml(joined)}</div>
        </div>
        <div class="member-actions">
          <select class="form-input" style="padding:2px 6px;font-size:11px;width:auto" data-member-id="${escAttr(m.person_id)}" data-member-role>
            <option value="owner" ${m.role === 'owner' ? 'selected' : ''}>Owner</option>
            <option value="admin" ${m.role === 'admin' ? 'selected' : ''}>Admin</option>
            <option value="member" ${m.role === 'member' ? 'selected' : ''}>Member</option>
            <option value="guest" ${m.role === 'guest' ? 'selected' : ''}>Guest</option>
          </select>
          <button class="btn btn-danger btn-sm" data-remove-member="${escAttr(m.person_id)}">Remove</button>
        </div>
      </div>
    `;
  }
  if (members.length === 0) {
    membersHtml += '<div class="cl-empty-msg">No members yet</div>';
  }
  membersHtml += '</div>';

  showModal(modalFrame(`Manage Members - ${escHtml(server.name)}`,
    `${membersHtml}
    <div style="margin-top:var(--space-4)">
      <button class="btn btn-secondary" id="modal-add-member">${iconUsers()} Add Member</button>
    </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Close</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', closeModal);

  // Role change
  document.querySelectorAll<HTMLSelectElement>('[data-member-role]').forEach(sel => {
    sel.addEventListener('change', async () => {
      const personId = sel.dataset['memberId'];
      if (!personId) return;
      try {
        await post('/api/server_members/update', { server_id: serverId, person_id: personId, role: sel.value });
        await loadAll();
        showToast('Role updated');
      } catch {
        showToast('Failed to update role', 'err');
      }
    });
  });

  // Remove member
  document.querySelectorAll<HTMLElement>('[data-remove-member]').forEach(btn => {
    btn.addEventListener('click', () => {
      const personId = btn.dataset['removeMember'];
      if (!personId) return;
      confirmModal('Remove Member', 'Are you sure you want to remove this member?', async () => {
        try {
          await post('/api/server_members/delete', { server_id: serverId, person_id: personId });
          await loadAll();
          showManageMembersModal(serverId); // Refresh the modal
          showToast('Member removed');
        } catch {
          showToast('Failed to remove member', 'err');
        }
      });
    });
  });

  // Add member button
  document.getElementById('modal-add-member')?.addEventListener('click', () => {
    showAddMemberModal(serverId);
  });
}

function showAddMemberModal(serverId: string): void {
  const existingMemberIds = state.serverMembers
    .filter(m => m.server_id === serverId)
    .map(m => m.person_id);
  const available = state.people.filter(p => !existingMemberIds.includes(p.id));

  let optionsHtml = '<option value="">Select a person...</option>';
  for (const p of available) {
    optionsHtml += `<option value="${escAttr(p.id)}">${escHtml(p.name)}</option>`;
  }

  showModal(modalFrame('Add Member',
    `<div class="form-group">
      <label class="form-label" for="f-add-person">Person</label>
      <select class="form-input" id="f-add-person">${optionsHtml}</select>
    </div>
    <div class="form-group">
      <label class="form-label" for="f-add-role">Role</label>
      <select class="form-input" id="f-add-role">
        <option value="member">Member</option>
        <option value="admin">Admin</option>
        <option value="guest">Guest</option>
      </select>
    </div>`,
    `<button class="btn btn-secondary" id="modal-cancel">Cancel</button>
     <button class="btn btn-primary" id="modal-save">Add</button>`
  ));

  document.getElementById('modal-close')?.addEventListener('click', closeModal);
  document.getElementById('modal-cancel')?.addEventListener('click', () => { closeModal(); showManageMembersModal(serverId); });
  document.getElementById('modal-save')?.addEventListener('click', async () => {
    const personId = (document.getElementById('f-add-person') as HTMLSelectElement).value;
    const role = (document.getElementById('f-add-role') as HTMLSelectElement).value;
    if (!personId) { showToast('Select a person', 'warn'); return; }
    try {
      await post('/api/server_members/add', { server_id: serverId, person_id: personId, role });
      closeModal();
      await loadAll();
      showManageMembersModal(serverId);
      showToast('Member added');
    } catch {
      showToast('Failed to add member', 'err');
    }
  });
}

// ── App Settings Modal ──

function showAppSettingsModal(): void {
  openUserSettings();
}

// ── Leave Server ──

function confirmLeaveServer(serverId: string): void {
  const name = getServerName(serverId);
  confirmModal('Leave Server', `Are you sure you want to leave "${name}"? You will need a new invite to rejoin.`, async () => {
    try {
      const ownerId = state.ownerId ?? state.people[0]?.id ?? '';
      await post('/api/server_members/delete', { server_id: serverId, person_id: ownerId });
      if (state.currentServer === serverId) {
        state.currentServer = null;
      }
      await loadAll();
      renderApp();
      showToast('Left server');
    } catch {
      showToast('Failed to leave server', 'err');
    }
  });
}

// ── Onboarding event handler ──

function setupOnboardingHandler(): void {
  window.addEventListener('hub-onboard-create', (async (e: Event) => {
    const detail = (e as CustomEvent<{ name: string; type: string }>).detail;
    let ownerId = state.people[0]?.id ?? '';
    if (!ownerId) {
      const person = await post<Person>('/api/people/add', { name: 'You', role: 'Owner' });
      ownerId = person.id;
    }
    await post<Server>('/api/servers/add', { name: detail.name, type: detail.type, icon: '', owner_id: ownerId });
    await loadAll();
    state.currentServer = state.servers[state.servers.length - 1]?.id ?? null;
    renderApp();
    showToast('Team created');
  }) as EventListener);
}

// ── Standalone check ──

async function checkStandalone(): Promise<void> {
  if (sessionStorage.getItem('banner-dismissed')) return;
  try {
    const res = await fetch('../hub-registry.json').catch(() => null);
    if (!res || !res.ok || !res.headers.get('content-type')?.includes('json')) {
      showBanner();
      return;
    }
    const reg = await res.json().catch(() => null) as Record<string, { id?: string }> | null;
    if (!reg || !Object.values(reg).some(e => e.id === 'hub-core')) {
      showBanner();
    }
    if (reg) {
      const hasCell = Object.values(reg).some(e => e.id === 'cell');
      state.cellAvailable = hasCell;
    }
  } catch {
    showBanner();
  }
}

function showBanner(): void {
  const el = document.getElementById('standalone-banner');
  if (!el) return;
  el.style.display = 'flex';
}

function detectIframe(): void {
  if (window.self !== window.top) {
    document.body.classList.add('in-iframe');
  }
}

/** Check if user is authenticated and Hub People API is accessible */
async function checkAuth(): Promise<boolean> {
  // First check hub-core session
  try {
    // This goes to hub-core (NOT hub-people) — always use root path
    const res = await fetch('/api/auth/verify', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      if (data.data?.identity_id) {
        state.ownerId = data.data.identity_id;

        // Find or create person in Hub People using hub-core identity_id directly
        try {
          const people = await get<Person[]>('/api/people');
          const me = people.find(p => p.id === state.ownerId);
          if (!me) {
            // Person doesn't exist with hub-core identity_id — create with that ID.
            // Avoid the literal 'User' fallback (Mitchell 2026-04-26): when
            // hub-core didn't return a display_name/slug, the placeholder leaked
            // straight into auto-generated server names ("User Space", "User's
            // Mirror") via the Rust provisioners and survived even after the
            // onboarding modal renamed people.name. Prefer a less-collidable
            // placeholder; the onboarding modal triggered by justSignedUp will
            // immediately overwrite it via /api/people/update, which now also
            // renames the auto-generated personal + mirror server rows.
            const name = data.data.display_name
              || data.data.slug
              || ('Member-' + String(state.ownerId || '').slice(0, 8));
            await post<Person>('/api/people/add', {
              id: state.ownerId,
              name,
              email: '',
              phone: '',
              role: 'owner',
            });
            justSignedUp = true;
            signupDefaultName = name;

            // Auto-create a default server (owner_id = hub-core identity_id)
            const servers = await get<Server[]>(serversUrl());
            if (servers.length === 0) {
              await post('/api/servers/add', {
                name: 'Hub Ecosystems',
                type: 'business',
                owner_id: state.ownerId,
                icon: '',
                description: 'Your hub. Your team. Your data.',
              });
            }
          }
        } catch { /* silent */ }
      }
    }
  } catch { /* no hub-core session */ }

  // Hub People now trusts the hub_session cookie directly
  return !!state.ownerId;
}

// showLogin() removed — all unauth users now go through the Discord-style
// landing page (pages/landing.ts) which includes sign-in, sign-up, and
// guest flows in one consistent surface. See Issue A in dev8 brief.

/** Reload the page as soon as a new service worker takes control. Without
 *  this, `skipWaiting()` + `clientsClaim()` activate the new SW but the tab
 *  keeps running the old in-memory JS — Mitchell hit this during batch 7
 *  smoke testing where the deployed bundle had the fix but the open tab did
 *  not. Guard with a module-scoped flag so the listener only fires once per
 *  session (the spec says controllerchange can fire more than once).
 *
 *  Defensive: the SW also force-navigates clients on activate (sw.ts), and
 *  in some browsers either the SW navigate or the location.reload wins
 *  first. To guarantee the banner doesn't get stuck on screen if the
 *  navigation never completes (e.g. a beforeunload prompt cancelled it),
 *  the banner self-clears after 3.5s. */
let swReloadArmed = false;
function armServiceWorkerReload(): void {
  if (swReloadArmed || !('serviceWorker' in navigator)) return;
  swReloadArmed = true;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // A new service worker took control \u2014 DON'T force-navigate (that kills an
    // active call and any half-typed message). Show the same non-destructive
    // Reload banner; the user updates when ready.
    showUpdateBanner();
  });
}

async function init(): Promise<void> {
  // Arm the SW-update → reload handler as early as possible so a
  // `controllerchange` that fires while init() is still running (e.g. on a
  // cold load where the SW activates during startup) is not missed.
  armServiceWorkerReload();

  // Register the service worker (network-first + push). Skip inside
  // native Capacitor/WebView shells — they handle caching + push natively.
  if ('serviceWorker' in navigator && !(window as unknown as Record<string, unknown>)['HubNative']) {
    navigator.serviceWorker.register('/people/sw.js').catch(() => {/* SW registration failed — non-fatal */});
  }

  // Restore theme
  const saved = localStorage.getItem('hub-people-theme');
  if (saved === 'light' || saved === 'dark') {
    state.theme = saved;
    document.documentElement.dataset['theme'] = saved;
  }

  // Restore server-rail collapsed state (1d) before first paint to avoid flash
  if (localStorage.getItem('hub-people-rail-collapsed') === 'true') {
    document.documentElement.classList.add('rail-collapsed');
  }

  detectIframe();

  // Public booking page (/book/<slug> or /people/book/<slug>) renders before
  // auth — we don't force visitors to sign in just to book a session.
  if (isPublicBookingPath()) {
    await renderBookingPublic();
    return;
  }

  // Public support-link redeem page (/s/<token>). Same shape as the
  // booking page above: visitors don't need a session to land here.
  if (isPublicSupportLinkPath()) {
    await renderSupportLink();
    return;
  }

  // Public NDIS invoice approval (/ndis/approve/<token>) — token IS the
  // credential. Provider reviews and approves/edits with no account.
  if (isNdisApprovePath()) {
    await renderNdisApprove();
    return;
  }

  // NDIS invoice console (/ndis/console) — admin invoice builder + profiles.
  // Uses the same-origin session cookie (Mitchell is signed in).
  if (isNdisConsolePath()) {
    await renderNdisConsole();
    return;
  }

  // Public agreement signing page (/agreements/sign/<token>). Token IS
  // the credential — no auth required. DocuSign 1:1 pattern.
  if (isPublicSigningPath()) {
    renderAgreementSign();
    return;
  }

  if (isNamedGuestPath()) {
    await renderNamedGuestPage();
    return;
  }

  // Support profile setup (draft V2 — public link, no auth)
  if (isSupportProfileSetupPath()) {
    renderSupportProfileSetup();
    return;
  }

  // Support onboarding combined flow (draft V2 — public link, no auth)
  if (isSupportOnboardingPath()) {
    renderSupportOnboarding();
    return;
  }

  // Support agreement draft-only view (draft V2 — public link, no auth)
  if (isSupportAgreementDraftPath()) {
    renderSupportAgreementDraftOnly();
    return;
  }

  // Support onboarding demo (mock data — no auth, no backend)
  if (isSupportOnboardingDemoPath()) {
    renderSupportOnboardingDemo();
    return;
  }

  if (isSupportWorkerOnboardingPath()) {
    renderSupportWorkerOnboarding();
    return;
  }

  if (isParticipantProfileBuilderPath()) {
    renderParticipantProfileBuilder();
    return;
  }

  if (isProviderProfileBuilderPath()) {
    renderProviderProfileBuilder();
    return;
  }

  if (isProviderInvoiceReviewPath()) {
    renderProviderInvoiceReview();
    return;
  }

  // NDIS plan code extraction (testing only — no auth)
  if (isNdisInvoiceDraftPath()) {
    renderNdisInvoiceDraft();
    return;
  }

  if (isNdisExtractionPath()) {
    renderNdisExtraction();
    return;
  }

  if (isReviewTokenPath()) {
    await renderReviewToken();
    return;
  }

  if (isGiteaBrowserPath()) {
    renderGiteaBrowser();
    return;
  }

  // Check auth first — show Discord-style landing if not authenticated.
  // All unauth users get the same landing page: root paths show
  // Sign Up + Guest; deep links (e.g. /channels/...) show Sign In so the
  // user can authenticate and return to their intended page.
  const authed = await checkAuth();
  if (!authed) {
    await renderLanding();
    return;
  }

  if (isGuestTestPath()) {
    renderGuestTestPage();
    return;
  }

  if (isGuestControlsTestPath()) {
    renderGuestControlsTestPage();
    return;
  }

  if (isAdminMigrationPath()) {
    renderAdminMigrationDashboard();
    return;
  }

  if (isGuestExperiencePath()) {
    renderGuestExperience();
    return;
  }

  await checkStandalone();
  setupOnboardingHandler();

  // Display-name onboarding (batch 7z item B). If checkAuth() created a
  // brand-new person row this load AND the user hasn't already been
  // onboarded on this device, intercept BEFORE the first render so the
  // skeleton shell doesn't flash behind a transparent overlay. The modal
  // sets the localStorage flag on Continue OR close — we don't re-prompt.
  if (justSignedUp && state.ownerId) {
    const flag = (() => {
      try { return localStorage.getItem(onboardingFlagKey(state.ownerId)); }
      catch { return null; }
    })();
    if (flag !== 'true') {
      await showDisplayNameOnboarding(signupDefaultName);
    }
  }

  // Show skeletons while loading
  renderApp();

  // Fetch feature flags once per session (fail-open: empty map on error)
  state.flags = await get<Record<string, boolean>>(
    `/api/feature-flags/me?person_id=${encodeURIComponent(state.ownerId || '')}`,
  ).catch(() => ({} as Record<string, boolean>));

  // Mobile bottom-tab nav removed per Mitchell 2026-04-20 — replaced with
  // Discord-style swipe drawers (initMobileSidebar). Force-hide any leftover
  // markup so cached HTML doesn't resurrect the bar.
  const mobileNav = document.getElementById('mobile-bottom-nav');
  if (mobileNav) { mobileNav.style.display = 'none'; mobileNav.remove(); }
  document.body.classList.remove('hp-dmv2');

  await loadAll();

  // Restore the user's place after a reload. Two sources:
  //   1. sessionStorage (same-tab refresh / auto-update — new tabs start clean)
  //   2. Hash fragment #s/SERVER_ID or #s/SERVER_ID/CHANNEL (open-in-new-tab)
  // Hash takes priority (explicit deep-link); sessionStorage is the fallback
  // for F5 / auto-update reloads within the same tab.
  try {
    const h = window.location.hash || '';
    const deepMatch = h.match(/^#s\/([^/]+)(?:\/(.+))?$/);
    const restoreServer = deepMatch?.[1] || sessionStorage.getItem('hp-restore-server');
    const restoreChannel = deepMatch?.[2] ? decodeURIComponent(deepMatch[2]) : sessionStorage.getItem('hp-restore-channel');
    if (restoreServer && state.servers.some(s => s.id === restoreServer)) {
      state.currentServer = restoreServer;
      await loadChannelsForServer(restoreServer);
      if (restoreChannel) {
        const serverChans = state.channels.filter(c => c.server_id === restoreServer);
        if (serverChans.some(c => c.name === restoreChannel)) {
          state.currentChannel = restoreChannel;
        }
      }
    }
    if (deepMatch) history.replaceState({}, '', window.location.pathname + window.location.search);
    sessionStorage.removeItem('hp-restore-server');
    sessionStorage.removeItem('hp-restore-channel');
  } catch { /* private mode / missing API */ }

  // Save place on ANY unload (F5, tab close, navigation) so same-tab
  // reloads restore position. sessionStorage is per-tab so new tabs
  // start clean.
  window.addEventListener('beforeunload', () => {
    try {
      if (state.currentServer) sessionStorage.setItem('hp-restore-server', state.currentServer);
      else sessionStorage.removeItem('hp-restore-server');
      if (state.currentChannel) sessionStorage.setItem('hp-restore-channel', String(state.currentChannel));
      else sessionStorage.removeItem('hp-restore-channel');
    } catch { /* quota */ }
  });

  renderApp();

  // Mount the global call overlay once. It renders nothing until an
  // outbound dial or inbound ring arrives on the `<server>:calls` SSE topic.
  mountCallOverlay();

  // PWA install banner — listens for `beforeinstallprompt`, defers the
  // native mini-infobar, and renders our own styled banner. No-op when
  // already installed (standalone display mode) or previously dismissed.
  initInstallBanner();

  // Invite preview (batch 7z) — show server icon / name / member count /
  // inviter BEFORE joining, Discord-style. Accepts both `?invite=CODE`
  // (legacy) and `#invite/CODE` (the format that vanity / generated links
  // produce). Decline simply cleans the URL; Accept POSTs the existing
  // /api/invites/join/<code> endpoint and falls through to loadAll +
  // renderApp.
  const urlParams = new URLSearchParams(window.location.search);
  let inviteCode = urlParams.get('invite');
  const hash = window.location.hash || '';
  if (!inviteCode && hash.startsWith('#invite/')) {
    inviteCode = hash.slice('#invite/'.length).split(/[?&#]/)[0] || null;
  }
  if (inviteCode && state.ownerId) {
    void showInvitePreview(inviteCode);
  }

  // Channel-list search button dispatches this so the jump wiring lives in
  // one place.
  const openSwitcherWithJump = (): void => {
    if (state.flags['hp_discord_desktop_v2'] === false) return;
    openQuickSwitcher((type, id) => {
      if (type === 'server') switchServer(id);
      else if (type === 'channel') switchChannel(id);
      else if (type === 'dm') {
        switchServer(null);
        requestAnimationFrame(() => openDmConversation(id));
      }
    });
  };
  document.addEventListener('hp:open-quick-switcher', openSwitcherWithJump);

  // Global keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    // Ctrl+, or Cmd+, — open user settings (Discord)
    if ((e.ctrlKey || e.metaKey) && e.key === ',') {
      e.preventDefault();
      openUserSettings();
      return;
    }
    // Ctrl+K or Cmd+K — quick switcher (Discord-style)
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      openSwitcherWithJump();
    }
    // Ctrl+Shift+M — toggle member list
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'M') {
      e.preventDefault();
      document.getElementById('toggle-member-list')?.click();
    }
    // Alt+Up/Down — switch channels
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const channels = document.querySelectorAll<HTMLElement>('.cl-channel');
      const activeIdx = [...channels].findIndex(el => el.classList.contains('active'));
      const nextIdx = e.key === 'ArrowDown'
        ? Math.min(activeIdx + 1, channels.length - 1)
        : Math.max(activeIdx - 1, 0);
      channels[nextIdx]?.click();
    }
    // Alt+Shift+Up/Down — switch servers
    if (e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const servers = document.querySelectorAll<HTMLElement>('.sr-icon[data-server]');
      const activeIdx = [...servers].findIndex(el => el.classList.contains('active'));
      const nextIdx = e.key === 'ArrowDown'
        ? Math.min(activeIdx + 1, servers.length - 1)
        : Math.max(activeIdx - 1, 0);
      servers[nextIdx]?.click();
    }
    // ? key OR Ctrl+/ \u2014 keyboard shortcuts cheatsheet. Ctrl+/ matches Discord
    // exactly (works even while typing in inputs). The bare ? still works
    // when no input is focused.
    if ((e.key === '/' && (e.ctrlKey || e.metaKey)) ||
        (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey &&
         (() => {
           const ae = document.activeElement;
           const tag = (ae?.tagName || '').toLowerCase();
           return tag !== 'input' && tag !== 'textarea' && !(ae as HTMLElement | null)?.isContentEditable;
         })())) {
      e.preventDefault();
      if (state.flags['hp_discord_desktop_v2'] !== false) {
        openKeyboardShortcuts();
      }
    }
  });

  // Global "open DM" route (1e/1f) — fired by member-list, profile-card,
  // and any other surface that wants to start a 1:1 conversation. Switches
  // to the DM home (overview) and opens the conversation with the partner.
  window.addEventListener('hub-open-dm', (e: Event) => {
    const detail = (e as CustomEvent<{ personId: string }>).detail;
    if (!detail || !detail.personId) return;
    switchServer(null);
    // Switch DOM render finished → open conversation in the same content area.
    requestAnimationFrame(() => openDmConversation(detail.personId));
  });
}

init();

// Export for channel renderers to trigger reload
export { loadAll, renderApp };

// ── Hub Footer Nav: check which apps are alive, dim dead ones ──
async function updateFooter(): Promise<void> {
  const footer = document.getElementById('app-footer');
  if (!footer || window.self !== window.top) {
    if (footer) footer.style.display = 'none';
    return;
  }
  const links = footer.querySelectorAll<HTMLAnchorElement>('.hub-footer-col a[data-app]');
  let aliveCount = 0;
  for (const link of links) {
    const port = new URL(link.href).port;
    try {
      const r = await fetch(`http://localhost:${port}/api/health`, { signal: AbortSignal.timeout(800) });
      if (r.ok) { link.classList.remove('dead'); aliveCount++; }
      else { link.classList.add('dead'); }
    } catch { link.classList.add('dead'); }
  }
  // Mark current app active
  for (const link of links) {
    if (link.dataset.app === 'people') link.classList.add('active');
  }
  footer.style.display = aliveCount > 1 ? '' : 'none';
}
setInterval(() => void updateFooter(), 15000);
setTimeout(() => void updateFooter(), 2000);

// Poll unreads every 10s for tab title + badges
setInterval(() => void loadUnreads(), 10000);

// ── Helpers ──

function escHtml(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// Mobile bottom-tab nav removed per Mitchell 2026-04-20. Discord-style
// swipe drawers (initMobileSidebar below) cover navigation; the hamburger in
// the server-rail and the people icon in the channel header open them.

// ── VirtualKeyboard API (Chromium mobile) ──
// When supported, flip overlaysContent = true so the soft keyboard stops
// pushing the viewport upwards. CSS then uses env(keyboard-inset-height)
// to pin the compose bar directly above the keyboard (see channels.css).
// Safari iOS has no VirtualKeyboard API — the visualViewport path below
// handles that case with a JS transform fallback.
(function initVirtualKeyboard(): void {
  const nav = navigator as unknown as {
    virtualKeyboard?: { overlaysContent: boolean };
  };
  if (nav.virtualKeyboard) {
    try { nav.virtualKeyboard.overlaysContent = true; } catch { /* noop */ }
  }
})();

// Apply a body-level class reflecting PWA standalone mode so CSS can hide
// install banners / browser chrome prompts and add safe-area padding. Kept
// live via the matchMedia change listener so hot-swap between tab and
// installed app works without a reload.
(function initStandaloneClass(): void {
  const mq = window.matchMedia('(display-mode: standalone)');
  const apply = (): void => {
    const on = mq.matches
      || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    document.body.classList.toggle('pwa-standalone', on);
  };
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else mq.addListener(apply);
})();

// Keep <meta name="theme-color"> in sync with the active theme so the mobile
// browser status-bar tint follows dark/light. Reads the current theme off
// the <html data-theme> attribute and updates on change.
(function initThemeColor(): void {
  const apply = (): void => {
    const theme = document.documentElement.dataset['theme'] === 'light' ? 'light' : 'dark';
    const color = theme === 'light' ? '#FFFBF5' : '#1a1a1a';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
  };
  apply();
  const mo = new MutationObserver(apply);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
})();

// ── Mobile soft-keyboard handling (visualViewport fallback) ──
// When the on-screen keyboard opens, visualViewport.height shrinks below
// window.innerHeight by the keyboard's height. Reflect that as a CSS var so
// the compose bar (and its ancestor #app) can stay anchored to the visible
// viewport bottom instead of being covered. Superseded by VirtualKeyboard
// API on Chromium — only iOS Safari still uses this.
(function initVisualViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const apply = (): void => {
    const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    document.documentElement.style.setProperty('--keyboard-offset', `${offset}px`);
  };
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  apply();
})();

// ── GLOBAL: keep the focused input above the soft keyboard (EVERY surface) ──
// Because virtualKeyboard.overlaysContent = true, the browser does NOT scroll a
// focused field into view when the keyboard opens — so on settings, modals and
// any form the field can end up hidden behind the keyboard. Bottom-pinned
// composers already self-lift via env(keyboard-inset-height); THIS covers
// everything else generically instead of per-view hacks: read the keyboard's
// top edge (VirtualKeyboard geometry on Chromium, visualViewport on iOS) and
// scroll the focused editable up only if it's occluded. Additive + a no-op when
// nothing is covered, so it never fights an already-visible pinned composer.
(function initKeepFocusAboveKeyboard(): void {
  type VK = { boundingRect?: DOMRectReadOnly; addEventListener?: (t: string, cb: () => void) => void };
  const vk = (navigator as unknown as { virtualKeyboard?: VK }).virtualKeyboard;

  const isEditable = (el: Element | null): el is HTMLElement =>
    !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
      || (el as HTMLElement).isContentEditable);

  // Y of the keyboard's top edge in client coords; Infinity when no keyboard up.
  const keyboardTop = (): number => {
    if (vk?.boundingRect && vk.boundingRect.height > 0) {
      return window.innerHeight - vk.boundingRect.height;
    }
    const vv = window.visualViewport;
    if (vv && window.innerHeight - vv.height > 100) return vv.height + vv.offsetTop;
    return Infinity;
  };

  const ensureVisible = (): void => {
    const el = document.activeElement;
    if (!isEditable(el)) return;
    const top = keyboardTop();
    if (!isFinite(top)) return;
    const r = el.getBoundingClientRect();
    if (r.bottom > top - 8) {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      catch { el.scrollIntoView(); }
    }
  };

  if (vk?.addEventListener) vk.addEventListener('geometrychange', () => window.setTimeout(ensureVisible, 60));
  window.visualViewport?.addEventListener('resize', () => window.setTimeout(ensureVisible, 60));
  // Focusing a field can open the keyboard with no geometry event on some
  // engines — re-check shortly after focus too.
  document.addEventListener('focusin', () => window.setTimeout(ensureVisible, 350));
})();

// ── Mobile sidebar toggle (Discord-style swipe) ──
(function initMobileSidebar(): void {
  const channelList = document.getElementById('channel-list');
  const scrim = document.getElementById('sidebar-scrim');
  const content = document.getElementById('content-area');
  const memberList = document.getElementById('member-list');

  if (!channelList || !scrim) return;

  // Gesture thresholds. Discord uses distance OR velocity — a quick flick
  // triggers even if the finger didn't travel far.
  const EDGE_ZONE = 40;        // px from viewport edge for edge-swipe open
  const DIST_TRIGGER = 60;     // px distance to commit
  const FLICK_DIST = 30;       // px minimum for a flick
  const FLICK_VEL = 0.5;       // px/ms velocity threshold for a flick
  const Y_TOLERANCE = 80;      // max vertical drift for a horizontal swipe

  const MOBILE_MAX = 768;

  function isMobile(): boolean {
    return window.innerWidth < MOBILE_MAX;
  }

  function isFormTarget(el: EventTarget | null): boolean {
    const t = el as Element | null;
    return !!t?.closest('input, textarea, select, [contenteditable="true"]');
  }

  function openSidebar(): void {
    if (isMobile() && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const ml = document.getElementById('member-list');
    if (ml?.classList.contains('mobile-open')) {
      ml.classList.remove('mobile-open');
      document.getElementById('members-toggle')?.classList.remove('active');
    }
    // Discord parity: the left drawer is server-rail + channel-list as ONE
    // unit. Mitchell 2026-04-25: "the server rail never tucks away" — fix
    // is to slide both in together when the hamburger fires (was channel-
    // list-only, server rail stayed glued at the left edge).
    document.getElementById('server-rail')?.classList.add('open');
    channelList?.classList.add('open');
    scrim?.classList.add('open');
    requestAnimationFrame(() => {
      const active = channelList?.querySelector('.cl-channel.active') as HTMLElement | null;
      if (active) active.scrollIntoView({ block: 'nearest' });
    });
  }

  function closeSidebar(): void {
    document.getElementById('server-rail')?.classList.remove('open');
    channelList?.classList.remove('open');
    document.getElementById('member-list')?.classList.remove('mobile-open');
    const btn = document.getElementById('members-toggle');
    if (btn && !document.getElementById('member-list')?.classList.contains('open')) {
      btn.classList.remove('active');
    }
    scrim?.classList.remove('open');
  }

  function openMemberDrawer(): void {
    const ml = document.getElementById('member-list');
    if (!ml) return;
    channelList?.classList.remove('open');
    ml.classList.add('mobile-open');
    scrim?.classList.add('open');
    document.getElementById('members-toggle')?.classList.add('active');
  }

  function closeMemberDrawer(): void {
    const ml = document.getElementById('member-list');
    if (!ml) return;
    ml.classList.remove('mobile-open');
    document.getElementById('members-toggle')?.classList.remove('active');
    if (!channelList?.classList.contains('open')) {
      scrim?.classList.remove('open');
    }
  }

  scrim.addEventListener('click', closeSidebar);

  // Shared touch tracking: source tells us which element's handlers are live;
  // nulling it on unrelated touches lets us share a small state block across
  // the three drag origins (content / channel-list / member-list).
  let startX = 0, startY = 0, lastX = 0, lastT = 0, vx = 0;
  let source: 'content' | 'channel' | 'member' | null = null;

  function begin(src: 'content' | 'channel' | 'member', t: Touch): void {
    startX = lastX = t.clientX;
    startY = t.clientY;
    lastT = performance.now();
    vx = 0;
    source = src;
  }

  function sample(t: Touch): void {
    const now = performance.now();
    const dt = Math.max(1, now - lastT);
    vx = (t.clientX - lastX) / dt;
    lastX = t.clientX;
    lastT = now;
  }

  // ── Edge-swipe to OPEN (on content area) ──
  content?.addEventListener('touchstart', (e: TouchEvent) => {
    if (!isMobile() || e.touches.length !== 1 || isFormTarget(e.target)) { source = null; return; }
    const t = e.touches[0];
    if (!t) return;
    begin('content', t);
  }, { passive: true });

  content?.addEventListener('touchmove', (e: TouchEvent) => {
    if (source !== 'content') return;
    const t = e.touches[0];
    if (t) sample(t);
  }, { passive: true });

  content?.addEventListener('touchend', (e: TouchEvent) => {
    if (source !== 'content') return;
    source = null;
    const endT = e.changedTouches[0];
    if (!endT) return;
    const dx = endT.clientX - startX;
    const dy = endT.clientY - startY;
    if (Math.abs(dy) > Y_TOLERANCE) return;
    if (startX < EDGE_ZONE && (dx > DIST_TRIGGER || (dx > FLICK_DIST && vx > FLICK_VEL))) {
      openSidebar();
      return;
    }
    if (startX > window.innerWidth - EDGE_ZONE
        && (dx < -DIST_TRIGGER || (dx < -FLICK_DIST && vx < -FLICK_VEL))
        && state.currentServer) {
      openMemberDrawer();
    }
  }, { passive: true });

  // ── Swipe-to-CLOSE (channel list drawer) ──
  channelList.addEventListener('touchstart', (e: TouchEvent) => {
    if (!isMobile() || !channelList.classList.contains('open') || e.touches.length !== 1) return;
    if (isFormTarget(e.target)) return;
    const t = e.touches[0];
    if (!t) return;
    begin('channel', t);
  }, { passive: true });

  channelList.addEventListener('touchmove', (e: TouchEvent) => {
    if (source !== 'channel') return;
    const t = e.touches[0];
    if (t) sample(t);
  }, { passive: true });

  channelList.addEventListener('touchend', (e: TouchEvent) => {
    if (source !== 'channel') return;
    source = null;
    const endT = e.changedTouches[0];
    if (!endT) return;
    const dx = endT.clientX - startX;
    const dy = endT.clientY - startY;
    if (Math.abs(dy) > Y_TOLERANCE) return;
    if (dx < -DIST_TRIGGER || (dx < -FLICK_DIST && vx < -FLICK_VEL)) closeSidebar();
  }, { passive: true });

  // ── Swipe-to-CLOSE (member list drawer) ──
  memberList?.addEventListener('touchstart', (e: TouchEvent) => {
    if (!isMobile() || !memberList.classList.contains('mobile-open') || e.touches.length !== 1) return;
    if (isFormTarget(e.target)) return;
    const t = e.touches[0];
    if (!t) return;
    begin('member', t);
  }, { passive: true });

  memberList?.addEventListener('touchmove', (e: TouchEvent) => {
    if (source !== 'member') return;
    const t = e.touches[0];
    if (t) sample(t);
  }, { passive: true });

  memberList?.addEventListener('touchend', (e: TouchEvent) => {
    if (source !== 'member') return;
    source = null;
    const endT = e.changedTouches[0];
    if (!endT) return;
    const dx = endT.clientX - startX;
    const dy = endT.clientY - startY;
    if (Math.abs(dy) > Y_TOLERANCE) return;
    if (dx > DIST_TRIGGER || (dx > FLICK_DIST && vx > FLICK_VEL)) closeMemberDrawer();
  }, { passive: true });

  // Close sidebar when channel is selected on mobile
  channelList.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.closest('.cl-channel') && window.innerWidth < MOBILE_MAX) {
      closeSidebar();
    }
  });

  // Hamburger toggles sidebar on mobile
  const rail = document.getElementById('server-rail');
  rail?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.closest('.sr-hamburger') && window.innerWidth < MOBILE_MAX) {
      e.stopPropagation();
      if (channelList.classList.contains('open')) closeSidebar();
      else openSidebar();
    }
  });
})();
